/**
 * HistoricalTimeline.jsx — Temporal Intelligence System
 *
 * FOUNDER DIRECTIVE: Pattern recognition layer.
 * Unified map + timeline where scrubbing through history reveals
 * failure as systemic, global, and recurring.
 *
 * Map = dominant. Timeline = control surface. Detail = consequential.
 * Cinematic, responsive, precise, alive.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate as animateMotion } from 'framer-motion';
import {
  ComposableMap, Geographies, Geography, Marker, ZoomableGroup
} from 'react-simple-maps';
import { Shield, X, ChevronLeft, ChevronRight, Play, Pause, ArrowUpRight, Check, MousePointerClick } from 'lucide-react';
import {
  useGranulloIntelligence,
  parseProgram, generateChecks,
  COLORS as SYS_COLORS,
} from '../../contexts/GranulloIntelligenceContext';
import { useTheme } from '../../contexts/ThemeContext';
import { RebelRedWordOverlay } from './SVGRebelKitV2';
import { lensAnnotation, LENS_TYPES } from '../lens-v2/LensAnnotations';
import FailureRecurrenceGraph from './FailureRecurrenceGraph';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

/* ═══════════════════════ RAW EVENT DATA ═══════════════════════ */
const RAW_EVENTS = [
  {
    year: 1920, entity: 'Charles Ponzi', amount: '$20M', category: 'Fabricated Records',
    country: 'United States', coords: [-71.0589, 42.3601],
    summary: 'Promised 50% returns in 45 days through postal reply coupons. No actual trades occurred.',
    broke: 'Returns fabricated. No underlying investment existed. New deposits paid old investors.',
    allowed: 'No verification of actual returns against market activity. Self-reported performance accepted.',
    cost: '$20 million lost. Term "Ponzi scheme" enters global vocabulary.',
    granullo: 'Investment returns require evidence-linked verification. No self-reported performance accepted without independent reconciliation.',
    prevention: '$20M investment fund, verified instruments only, after independent return verification and evidence reconciliation',
  },
  {
    year: 1929, entity: 'Wall Street Crash', amount: '$30B', category: 'Unverified Leverage',
    country: 'United States', coords: [-74.0112, 40.7068],
    summary: 'Margin lending at 10:1 ratios fueled speculative bubble. Market lost 89% over three years.',
    broke: 'No structural limit on margin leverage. Broker loans exceeded GDP. No circuit breakers.',
    allowed: 'Leverage ratios were unlimited. Borrowed money purchased speculative assets without constraint.',
    cost: '$30 billion in market value destroyed in weeks. Global depression lasting a decade.',
    granullo: 'Leverage ratios have structural binding limits. Margin requirements are enforced before position creation, not after collapse.',
    prevention: '$30B market fund, verified margin only, after leverage ratio verification and position limit checks',
  },
  {
    year: 1970, entity: 'Penn Central', amount: '$3.3B', category: 'Hidden Exposure',
    country: 'United States', coords: [-75.1652, 39.9526],
    summary: 'Largest US bankruptcy at the time. Executives hid losses through fraudulent accounting while issuing dividends.',
    broke: 'Financial statements concealed operating losses. Dividends paid from borrowed money.',
    allowed: 'No structural transparency. Board approved dividends without verification of actual cash position.',
    cost: '$3.3 billion in assets at bankruptcy. Triggered commercial paper market crisis.',
    granullo: 'Dividend payments require verified positive cash position. No distribution without structural confirmation of available surplus.',
    prevention: '$3.3B corporate fund, verified cash positions only, after surplus verification and governance approval',
  },
  {
    year: 1982, entity: 'Continental Illinois', amount: '$40B', category: 'Broken Segregation',
    country: 'United States', coords: [-87.6298, 41.8781],
    summary: 'Seventh-largest US bank collapsed after concentrated exposure to Penn Square energy loans.',
    broke: 'Loan portfolio concentrated in single sector. Risk assessment delegated to originating bank without verification.',
    allowed: 'No structural diversification requirement. Purchased loans accepted at face value without independent credit review.',
    cost: '$40 billion in assets. First "too big to fail" bailout. FDIC absorbed $1.1 billion in losses.',
    granullo: 'Concentration limits are enforced automatically. Purchased loan portfolios require independent credit checks before acquisition.',
    prevention: '$40B banking fund, diversified portfolios only, after concentration limit verification and independent credit review',
  },
  {
    year: 1995, entity: 'Barings Bank', amount: '£827M', category: 'Broken Segregation',
    country: 'Singapore', coords: [103.8198, 1.3521],
    summary: 'Unauthorized derivatives trades by a single trader destroyed Britain\'s oldest merchant bank.',
    broke: 'No position limits enforced. No segregation between trading and settlement. Risk reports bypassed.',
    allowed: 'One individual controlled both trading and error reporting. No structural separation existed.',
    cost: '£827 million in losses hidden across error accounts. Bank declared insolvent.',
    granullo: 'Position limits are binding constraints. Settlement requires independent verification. No single actor bypasses structural controls.',
    prevention: '$827M derivatives fund, approved exchanges only, after independent position verification and settlement confirmation',
  },
  {
    year: 2001, entity: 'Enron', amount: '$74B', category: 'Hidden Exposure',
    country: 'United States', coords: [-95.3698, 29.7604],
    summary: 'Off-balance-sheet entities hid billions in debt while executives cashed out.',
    broke: 'Special purpose entities created without governance oversight. Debt obligations hidden from shareholders.',
    allowed: 'No structural visibility into off-balance-sheet entities. Governance had no access to actual debt positions.',
    cost: '$74 billion in shareholder value destroyed. Pension funds wiped out.',
    granullo: 'Every financial entity has declared obligations visible to stakeholders. Debt cannot be hidden from governance participants.',
    prevention: '$74B corporate fund, approved operations only, after independent governance review and debt disclosure verification',
  },
  {
    year: 2002, entity: 'WorldCom', amount: '$11B', category: 'Fabricated Records',
    country: 'United States', coords: [-89.7, 32.3],
    summary: 'Operating expenses reclassified as capital expenditures to inflate reported earnings.',
    broke: 'Accounting entries made without independent verification. Internal audit bypassed by executive override.',
    allowed: 'A single executive could override internal audit. No structural check existed for reclassification.',
    cost: '$11 billion in fraudulent accounting entries. $180 billion market cap erased.',
    granullo: 'Financial reclassifications require multi-party approval. No executive override without structural audit trail.',
    prevention: '$11B accounting fund, verified categories only, after independent audit verification and multi-party approval',
  },
  {
    year: 2008, entity: 'Lehman Brothers', amount: '$639B', category: 'Unverified Leverage',
    country: 'United States', coords: [-74.006, 40.7128],
    summary: 'Repo 105 transactions temporarily moved assets off balance sheet to hide leverage ratios.',
    broke: 'Repurchase agreements used as accounting devices without disclosure. Leverage concealed from regulators.',
    allowed: 'Accounting rules permitted temporary asset removal without disclosure. No structural transparency requirement.',
    cost: '$639 billion in assets at time of bankruptcy. Largest filing in U.S. history.',
    granullo: 'Leverage ratios are computed from committed positions, not reported positions. Temporary transfers are always visible.',
    prevention: '$639B asset fund, regulated counterparties only, after leverage ratio verification and regulatory disclosure',
  },
  {
    year: 2008, entity: 'Madoff Securities', amount: '$65B', category: 'Fabricated Records',
    country: 'United States', coords: [-73.95, 40.78],
    summary: 'Fabricated returns for 17 years. No actual trades executed. Statements were fictional.',
    broke: 'No independent custody verification. Returns never reconciled against market activity.',
    allowed: 'Self-reported returns accepted without verification. No independent custody confirmation required.',
    cost: '$65 billion in fabricated client balances. Actual losses approximately $17.5 billion.',
    granullo: 'Returns require evidence-linked verification. Custody is independently attested. No single entity controls both execution and reporting.',
    prevention: '$65B client fund, verified custodians only, after independent trade reconciliation and custody confirmation',
  },
  {
    year: 2011, entity: 'MF Global', amount: '$1.6B', category: 'Misused Funds',
    country: 'United States', coords: [-74.05, 40.7],
    summary: 'Customer kept separate funds used for company European sovereign debt bets.',
    broke: 'Segregation rules violated. Customer funds treated as firm capital. Internal controls overridden by CEO.',
    allowed: 'CEO had authority to override segregation controls. No structural barrier prevented commingling.',
    cost: '$1.6 billion in customer funds missing. Firm collapsed in four days.',
    granullo: 'Segregated funds have binding constraints. No executive can redirect funds designated for customer accounts.',
    prevention: '$1.6B kept separate customer fund, client-approved transactions only, after segregation verification',
  },
  {
    year: 2014, entity: 'Mt. Gox', amount: '$450M', category: 'Broken Custody',
    country: 'Japan', coords: [139.6917, 35.6895],
    summary: '850,000 Bitcoin lost through years of undetected theft from hot wallets.',
    broke: 'Private keys stored without multi-signature requirements. No real-time reconciliation. Audit was manual.',
    allowed: 'Single-key custody accepted as standard. No structural requirement for continuous reconciliation.',
    cost: '$450 million in customer Bitcoin. 24,000 customers affected.',
    granullo: 'Custody requires multi-party key management. Balances are reconciled continuously. No single point of custodial failure.',
    prevention: '$450M custody fund, multi-signature wallets only, after real-time balance reconciliation and key verification',
  },
  {
    year: 2016, entity: '1MDB', amount: '$4.5B', category: 'Misused Funds',
    country: 'Malaysia', coords: [101.6869, 3.139],
    summary: 'Sovereign wealth fund used as personal account by officials and bankers.',
    broke: 'No independent oversight of fund disbursements. Transfers approved without beneficiary verification.',
    allowed: 'Fund governance was nominal. Disbursements required no structural verification of purpose or recipient.',
    cost: '$4.5 billion diverted across 6 countries. Funded luxury real estate, films, art.',
    granullo: 'Every disbursement requires verified beneficiary and purpose. Sovereign funds have structural spending constraints that cannot be overridden.',
    prevention: '$4.5B sovereign fund, verified national programs only, after beneficiary verification and independent oversight approval',
  },
  {
    year: 2018, entity: 'Danske Bank', amount: '€200B', category: 'Hidden Exposure',
    country: 'Denmark', coords: [12.5683, 55.6761],
    summary: 'Estonian branch processed €200B in suspicious transactions over 8 years.',
    broke: 'Branch operated without central compliance oversight. Transaction monitoring was nominal.',
    allowed: 'Branch autonomy permitted local operations without central structural oversight or pattern detection.',
    cost: '€200 billion in suspicious flows. Criminal investigations across 6 jurisdictions.',
    granullo: 'All branches operate under unified compliance rules. Transaction patterns are monitored against declared purpose. Structural anomaly detection, not manual review.',
    prevention: '€200B branch operations fund, declared-purpose transactions only, after central compliance verification',
  },
  {
    year: 2018, entity: 'Theranos', amount: '$10B', category: 'Fabricated Records',
    country: 'United States', coords: [-122.0322, 37.3688],
    summary: 'Valued at $10B on fabricated diagnostic capabilities that never existed.',
    broke: 'Investor due diligence accepted self-reported metrics. No independent verification of technology claims.',
    allowed: 'Capital was deployed based on claims, not evidence. No structural verification of capability existed.',
    cost: '$10 billion in peak valuation destroyed. Patients received unreliable medical results.',
    granullo: 'Capital deployment requires verified evidence of capability. Self-reported metrics are never sufficient. Structural verification before disbursement.',
    prevention: '$10B investment fund, verified R&D milestones only, after independent capability verification and evidence review',
  },
  {
    year: 2020, entity: 'Wirecard', amount: '€1.9B', category: 'Fabricated Records',
    country: 'Germany', coords: [11.582, 48.1351],
    summary: '€1.9B in cash balances that simply did not exist. Auditor failed to verify.',
    broke: 'Third-party accounts accepted without independent confirmation. Auditor relied on management representations.',
    allowed: 'Auditor accepted management claims as evidence. No structural requirement for independent balance confirmation.',
    cost: '€1.9 billion missing. €13 billion in market value lost. DAX member collapse.',
    granullo: 'Cash balances require independent custodial confirmation. No balance is accepted without structural proof of existence.',
    prevention: '€1.9B escrow fund, verified custodians only, after independent balance confirmation and proof of existence',
  },
  {
    year: 2021, entity: 'Greensill Capital', amount: '$10B', category: 'Premature Release',
    country: 'United Kingdom', coords: [-0.1276, 51.5074],
    summary: 'Supply chain finance based on phantom future invoices that never materialized.',
    broke: 'Financing extended against predicted receivables. No verification that underlying transactions were real.',
    allowed: 'Future revenue accepted as collateral. No structural requirement that receivables be evidence-linked.',
    cost: '$10 billion in exposure. Credit Suisse funds frozen. 50,000 jobs at risk at Liberty Steel.',
    granullo: 'Financing requires evidence-linked receivables. Predicted revenue is not collateral. Structural verification of underlying transactions before release.',
    prevention: '$10B supply chain fund, verified invoices only, after receivable confirmation and transaction evidence review',
  },
  {
    year: 2022, entity: 'FTX', amount: '$8B', category: 'Broken Segregation',
    country: 'Bahamas', coords: [-77.3963, 25.0343],
    summary: 'Customer deposits mixed with company trading firm. Used for ventures, real estate, political donations.',
    broke: 'No structural segregation between customer and firm funds. Internal accounting was a spreadsheet.',
    allowed: 'No structural barrier between customer and company funds. A spreadsheet was the only record.',
    cost: '$8 billion in customer funds missing. Over 1 million creditors.',
    granullo: 'Customer funds are kept separate. No one can access protected funds for other purposes. Real-time balance checks required.',
    prevention: '$8B customer deposit fund, customer-approved withdrawals only, after segregation verification and balance confirmation',
  },
  {
    year: 2023, entity: 'Silicon Valley Bank', amount: '$209B', category: 'Hidden Exposure',
    country: 'United States', coords: [-121.8863, 37.3382],
    summary: '$209B bank collapsed in 48 hours when depositors realized unreported bond losses exceeded capital.',
    broke: 'Held-to-maturity accounting hid real losses. No requirement to disclose mark-to-market on HTM portfolio.',
    allowed: 'Accounting classification legally concealed economic reality. No structural transparency to stakeholders.',
    cost: '$209 billion in assets. Largest bank failure since 2008. FDIC intervention required.',
    granullo: 'All positions are valued at market. Accounting classification cannot hide economic reality. Stakeholders see true exposure at all times.',
    prevention: '$209B bank asset fund, mark-to-market positions only, after real-time exposure disclosure and capital verification',
  },
  {
    year: 2023, entity: 'Credit Suisse', amount: 'CHF 17B', category: 'Unverified Leverage',
    country: 'Switzerland', coords: [8.5417, 47.3769],
    summary: 'Decades of risk failures culminated in forced merger after Archegos and Greensill losses.',
    broke: 'Risk limits repeatedly overridden. Client concentration exceeded safe thresholds without consequence.',
    allowed: 'Risk limits were advisory, not structural. Overrides required no independent approval or audit trail.',
    cost: 'CHF 17 billion in AT1 bonds wiped out. 167-year institution absorbed by UBS.',
    granullo: 'Risk limits are structural, not advisory. Concentration breaches trigger automatic restrictions. No override without governance approval and audit trail.',
    prevention: 'CHF 17B risk fund, diversified counterparties only, after concentration limit verification and governance approval',
  },
  {
    year: 2023, entity: '79th Group', amount: 'Undisclosed', category: 'Premature Release',
    country: 'United States', coords: [-87.6298, 41.8781],
    summary: 'Premature capital release before verification conditions were met.',
    broke: 'Disbursement occurred before milestone confirmation. Manual approval process skipped structural checks.',
    allowed: 'Manual approval could bypass milestone gates. No structural requirement that evidence precede release.',
    cost: 'Undisclosed losses. Capital released without evidence of completion.',
    granullo: 'Release requires programmatic milestone verification. No manual override of structural conditions. Evidence must be submitted and validated before funds move.',
    prevention: '$5M milestone fund, verified contractors only, after milestone completion and evidence submission',
  },
  /* ═══════════ EXPANDED EVENTS — REAL WORLD FINANCIAL FAILURES ═══════════ */
  {
    year: 1991, entity: 'BCCI', amount: '$20B', category: 'Hidden Exposure',
    country: 'Luxembourg', coords: [6.1319, 49.6117],
    summary: 'Bank of Credit and Commerce International operated a global fraud spanning 78 countries.',
    broke: 'Fictitious loans, nominee accounts, and shell companies hid true bank exposure from every regulator.',
    allowed: 'Multi-jurisdictional structure meant no single regulator had full visibility. Complexity was the camouflage.',
    cost: '$20 billion in assets seized. 1.4 million depositors affected across 78 countries.',
    granullo: 'All entities in a financial structure have declared, visible obligations. No shell layer can hide exposure from governance participants.',
    prevention: '$20B banking fund, verified counterparties only, after unified compliance verification across all jurisdictions',
  },
  {
    year: 1994, entity: 'Orange County', amount: '$1.7B', category: 'Unverified Leverage',
    country: 'United States', coords: [-117.8311, 33.7175],
    summary: 'County treasurer leveraged public investment pool 3:1 using interest rate derivatives.',
    broke: 'No leverage limits on public fund management. Derivatives positions unmonitored by oversight body.',
    allowed: 'Treasurer had unilateral authority. No structural constraint on leverage ratios for public money.',
    cost: '$1.7 billion in losses. Largest municipal bankruptcy in U.S. history at the time.',
    granullo: 'Public fund leverage has structural binding limits. No single official can exceed risk parameters without multi-party governance approval.',
    prevention: '$1.7B public fund, approved instruments only, after leverage ratio verification and oversight approval',
  },
  {
    year: 1996, entity: 'Sumitomo Corporation', amount: '$2.6B', category: 'Broken Segregation',
    country: 'Japan', coords: [139.7525, 35.6852],
    summary: 'Rogue copper trader accumulated unauthorized positions over a decade.',
    broke: 'Trading positions not reconciled against authorized limits. Single trader controlled both execution and reporting.',
    allowed: 'No separation between trading authority and position reporting. Manual reconciliation missed structural violations.',
    cost: '$2.6 billion in trading losses hidden for 10 years.',
    granullo: 'Position limits are binding. Trading and reporting are kept separate. No single person can control both.',
    prevention: '$2.6B trading fund, approved exchanges only, after position limit verification and independent reconciliation',
  },
  {
    year: 1997, entity: 'Bre-X Minerals', amount: '$6B', category: 'Fabricated Records',
    country: 'Indonesia', coords: [116.0, 1.5],
    summary: 'Gold samples salted with external gold to fabricate the largest gold deposit ever discovered.',
    broke: 'Assay results accepted without independent verification. Due diligence relied on self-reported samples.',
    allowed: 'Investors deployed $6B based on unverified claims. No structural requirement for independent geological confirmation.',
    cost: '$6 billion in market value evaporated overnight. Multiple investor suicides.',
    granullo: 'Capital deployment requires independently verified evidence. Self-reported metrics are never sufficient for fund release.',
    prevention: '$6B mineral fund, verified operations only, after independent geological confirmation and evidence review',
  },
  {
    year: 1998, entity: 'LTCM', amount: '$4.6B', category: 'Unverified Leverage',
    country: 'United States', coords: [-73.6918, 41.0534],
    summary: 'Long-Term Capital Management leveraged $4.7B in capital to $125B in assets, nearly collapsing global markets.',
    broke: 'No structural limit on fund leverage. Counterparty risk concentrated without visibility.',
    allowed: 'Leverage ratio of 25:1 was treated as a strategy, not a structural risk. No binding constraint existed.',
    cost: '$4.6 billion in losses. Federal Reserve coordinated $3.6B bailout to prevent systemic collapse.',
    granullo: 'Leverage ratios have structural binding limits. Counterparty concentration triggers automatic restrictions before systemic thresholds.',
    prevention: '$4.6B fund, diversified counterparties only, after leverage verification and concentration limit checks',
  },
  {
    year: 2002, entity: 'Tyco International', amount: '$600M', category: 'Misused Funds',
    country: 'United States', coords: [-73.2, 40.8],
    summary: 'CEO and CFO stole $600M through unauthorized bonuses, loans, and stock fraud.',
    broke: 'Executive compensation bypassed board approval. Loans issued to executives without governance oversight.',
    allowed: 'Executive authority permitted self-enrichment without structural controls. Board was nominal.',
    cost: '$600 million stolen directly. $80 billion in shareholder value destroyed.',
    granullo: 'Executive compensation requires multi-party governance approval. No self-authorized loans or transfers. Structural audit trail on all executive transactions.',
    prevention: '$600M executive fund, board-approved disbursements only, after governance verification and compensation audit',
  },
  {
    year: 2003, entity: 'Parmalat', amount: '\u20AC14B', category: 'Fabricated Records',
    country: 'Italy', coords: [10.3279, 44.8015],
    summary: 'Forged bank documents to hide \u20AC14B hole in company accounts for over a decade.',
    broke: 'Bank confirmation letters forged. Auditors accepted management representations without independent verification.',
    allowed: 'A single forged letter was enough to satisfy audit requirements. No structural verification of bank balances.',
    cost: '\u20AC14 billion in fabricated assets. Europe\'s largest corporate bankruptcy.',
    granullo: 'Bank balances require direct institutional confirmation. No intermediary documents accepted. Structural proof of existence before any reporting.',
    prevention: '\u20AC14B corporate fund, verified custodians only, after independent balance confirmation and institutional confirmation',
  },
  {
    year: 2004, entity: 'HealthSouth', amount: '$2.7B', category: 'Fabricated Records',
    country: 'United States', coords: [-86.8025, 33.5207],
    summary: 'Earnings inflated by $2.7B through fabricated accounting entries directed by CEO.',
    broke: 'Accounting entries created without verification. CEO directed subordinates to adjust numbers monthly.',
    allowed: 'No structural check prevented executive-directed accounting manipulation. Entries flowed directly to reports.',
    cost: '$2.7 billion in fabricated earnings. Stock dropped from $30 to $0.06.',
    granullo: 'Financial entries require independent verification at creation. No executive can direct adjustments without structural audit trail and approval chain.',
    prevention: '$2.7B accounting fund, verified entries only, after independent verification and multi-party approval',
  },
  {
    year: 2005, entity: 'Refco', amount: '$430M', category: 'Hidden Exposure',
    country: 'United States', coords: [-74.0, 40.71],
    summary: 'CEO hid $430M in bad debts through end-of-quarter transactions with a related entity.',
    broke: 'Related-party transactions not disclosed. Quarter-end manipulations masked true exposure.',
    allowed: 'No structural transparency requirement for related-party flows. Timing manipulation was undetectable.',
    cost: '$430 million in hidden losses. Firm collapsed 2 months after IPO.',
    granullo: 'Related-party transactions require structural disclosure. Quarter-end flows are automatically flagged. No entity can mask exposure through timing.',
    prevention: '$430M brokerage fund, disclosed counterparties only, after related-party verification and timing analysis',
  },
  {
    year: 2007, entity: 'Northern Rock', amount: '\u00A3107B', category: 'Unverified Leverage',
    country: 'United Kingdom', coords: [-1.6178, 54.9783],
    summary: 'UK bank funded long-term mortgages with short-term wholesale borrowing, collapsed during credit crunch.',
    broke: 'Asset-liability mismatch unmonitored. Wholesale funding dependency created structural fragility.',
    allowed: 'No binding constraint on funding maturity mismatch. Liquidity risk was reported, not enforced.',
    cost: '\u00A3107 billion in assets. First UK bank run in 140 years. Nationalized.',
    granullo: 'Funding maturity requirements are structural. Asset-liability gaps trigger automatic restrictions. Liquidity risk is enforced, not just reported.',
    prevention: '\u00A3107B mortgage fund, matched-maturity funding only, after liquidity verification and maturity gap analysis',
  },
  {
    year: 2008, entity: 'AIG', amount: '$180B', category: 'Hidden Exposure',
    country: 'United States', coords: [-74.006, 40.7128],
    summary: 'Sold $500B in credit default swaps without adequate reserves, requiring $180B government bailout.',
    broke: 'CDS obligations accumulated without reserve requirements. Counterparty risk invisible to regulators.',
    allowed: 'Insurance-style obligations sold without insurance-style capital requirements. No structural reserve mandate.',
    cost: '$180 billion in federal bailout. Largest corporate rescue in history.',
    granullo: 'Obligations require proportional reserves. Counterparty exposure is visible to all stakeholders. No accumulation without structural capital backing.',
    prevention: '$180B insurance fund, verified obligations only, after reserve adequacy verification and counterparty exposure disclosure',
  },
  {
    year: 2009, entity: 'Stanford Financial', amount: '$8B', category: 'Fabricated Records',
    country: 'Antigua and Barbuda', coords: [-61.7964, 17.0608],
    summary: '$8B Ponzi scheme disguised as certificates of deposit with impossibly consistent returns.',
    broke: 'Returns fabricated monthly. No independent verification of underlying investments.',
    allowed: 'Offshore jurisdiction reduced regulatory scrutiny. Self-reported returns accepted without evidence.',
    cost: '$8 billion in investor funds. 28,000 investors defrauded.',
    granullo: 'Investment returns require evidence-linked verification. Self-reporting is never sufficient. Structural reconciliation against market reality.',
    prevention: '$8B investment fund, verified custodians only, after independent return reconciliation and evidence verification',
  },
  {
    year: 2010, entity: 'Satyam Computer', amount: '$1.5B', category: 'Fabricated Records',
    country: 'India', coords: [78.4867, 17.385],
    summary: 'Chairman fabricated $1.5B in cash reserves that never existed. Called India\'s Enron.',
    broke: 'Bank balance confirmations forged. Revenue inflated through fake invoices. Auditors did not independently verify.',
    allowed: 'Management-produced documents accepted as evidence. No structural mechanism for independent balance confirmation.',
    cost: '$1.5 billion in fabricated assets. 53,000 employees affected. Market cap collapsed 90%.',
    granullo: 'Cash balances require independent custodial confirmation. Revenue requires evidence-linked transaction trails. No management representation substitutes for structural proof.',
    prevention: '$1.5B corporate fund, verified custodians only, after independent balance confirmation and revenue verification',
  },
  {
    year: 2012, entity: 'JPMorgan London Whale', amount: '$6.2B', category: 'Broken Segregation',
    country: 'United Kingdom', coords: [-0.0877, 51.5074],
    summary: 'CIO office accumulated massive synthetic credit positions, losing $6.2B when market moved.',
    broke: 'Risk models changed to hide growing positions. VaR calculations adjusted to understate risk.',
    allowed: 'Risk model changes required no independent approval. Trading desk could modify its own risk metrics.',
    cost: '$6.2 billion in trading losses. $920 million in regulatory fines.',
    granullo: 'Risk model changes require independent governance approval. No desk modifies its own risk metrics. Structural separation between risk-taking and risk measurement.',
    prevention: '$6.2B trading fund, approved strategies only, after independent risk verification and model governance',
  },
  {
    year: 2012, entity: 'Libor Scandal', amount: '$9B+', category: 'Fabricated Records',
    country: 'United Kingdom', coords: [-0.1276, 51.5074],
    summary: 'Global banks systematically manipulated benchmark interest rates affecting $350T in derivatives.',
    broke: 'Rate submissions were self-reported. No independent verification of submitted rates.',
    allowed: 'Banks self-reported rates that determined their own trading profits. No structural integrity check on submissions.',
    cost: '$9 billion in fines across institutions. $350 trillion in affected contracts.',
    granullo: 'Rate submissions require independently verifiable data. No self-reported metric can determine financial outcomes without structural validation.',
    prevention: '$9B benchmark fund, verified data sources only, after independent rate validation and anti-manipulation checks',
  },
  {
    year: 2013, entity: 'Bangladesh Bank Heist (Preview)', amount: '$81M', category: 'Broken Custody',
    country: 'Bangladesh', coords: [90.4074, 23.8103],
    summary: 'Hackers compromised SWIFT credentials and stole $81M from Bangladesh central bank reserves at NY Fed.',
    broke: 'SWIFT access not multi-party controlled. Transfer authorization had insufficient verification layers.',
    allowed: 'Single-point credential access was sufficient for multi-million dollar transfers. No structural multi-party requirement.',
    cost: '$81 million stolen. $850 million more attempted. Exposed systemic SWIFT vulnerabilities.',
    granullo: 'Reserve transfers require multi-party authorization. No single credential can initiate movement. Structural verification at every transfer layer.',
    prevention: '$81M reserve fund, multi-party authorized transfers only, after credential verification and anomaly detection',
  },
  {
    year: 2015, entity: 'Toshiba', amount: '$1.2B', category: 'Fabricated Records',
    country: 'Japan', coords: [139.6917, 35.6895],
    summary: 'Profits overstated by $1.2B over 7 years through systematic accounting manipulation.',
    broke: 'Management pressure created culture of falsification. Internal controls overridden top-down.',
    allowed: 'Corporate hierarchy permitted systematic override of accounting controls. No structural independence of financial reporting.',
    cost: '$1.2 billion in overstated profits. Stock dropped 40%. CEO and seven others resigned.',
    granullo: 'Financial reporting has structural independence from management pressure. No hierarchical override of accounting controls without audit trail.',
    prevention: '$1.2B corporate fund, verified financials only, after independent audit verification and structural reporting checks',
  },
  {
    year: 2016, entity: 'Wells Fargo', amount: '$3.7B', category: 'Misused Funds',
    country: 'United States', coords: [-122.4194, 37.7749],
    summary: '3.5 million fake accounts created to meet sales targets. Customers charged for services never requested.',
    broke: 'No structural verification that account openings were customer-initiated. Sales incentives misaligned with controls.',
    allowed: 'Employees could open accounts without customer verification. No structural check against unauthorized account creation.',
    cost: '$3.7 billion in fines. 5,300 employees fired. CEO resigned. Brand permanently damaged.',
    granullo: 'Account creation requires verified customer authorization. No account can be opened without structural customer consent verification.',
    prevention: '$3.7B customer protection fund, customer-authorized accounts only, after identity verification and consent confirmation',
  },
  {
    year: 2017, entity: 'Abraaj Group', amount: '$1B', category: 'Misused Funds',
    country: 'United Arab Emirates', coords: [55.2708, 25.2048],
    summary: 'Largest PE firm in Middle East used healthcare fund money for corporate expenses and other deals.',
    broke: 'Fund-level segregation violated. LP capital redirected without disclosure. No real-time fund tracking.',
    allowed: 'GP had unilateral access to LP capital. No structural barrier between fund and corporate accounts.',
    cost: '$1 billion in misappropriated capital. $13.6 billion firm collapsed.',
    granullo: 'LP capital is kept separate. GP cannot redirect fund capital without LP approval. Real-time fund tracking with binding rules.',
    prevention: '$1B PE fund, LP-approved deployments only, after segregation verification and capital tracking',
  },
  {
    year: 2019, entity: 'Luckin Coffee', amount: '$310M', category: 'Fabricated Records',
    country: 'China', coords: [118.0894, 24.4798],
    summary: 'Fabricated $310M in sales through fake transactions and inflated customer numbers.',
    broke: 'Revenue recognized from fictitious transactions. No independent verification of reported sales.',
    allowed: 'Self-reported transaction data flowed directly to financial statements without structural verification.',
    cost: '$310 million in fabricated revenue. Nasdaq delisted. $12 billion market cap destroyed.',
    granullo: 'Revenue requires evidence-linked transactions. No self-reported data accepted without independent reconciliation. Structural verification before financial reporting.',
    prevention: '$310M revenue fund, verified transactions only, after independent sales reconciliation and evidence verification',
  },
  {
    year: 2020, entity: 'FinCEN Files', amount: '$2T+', category: 'Hidden Exposure',
    country: 'United States', coords: [-77.0369, 38.9072],
    summary: 'Leaked files revealed banks flagged $2T in suspicious transactions but continued processing them.',
    broke: 'Suspicious activity reports filed but no structural enforcement. Banks flagged but did not block.',
    allowed: 'Compliance was performative. Filing a report satisfied the requirement, regardless of action taken.',
    cost: '$2 trillion+ in suspicious transactions processed despite being flagged.',
    granullo: 'Flagged transactions are automatically blocked until cleared. Compliance is enforcement, not documentation. Filing a report triggers action, not just a record.',
    prevention: '$2T+ transaction flows, verified purpose only, after compliance verification and structural blocking of flagged transactions',
  },
  {
    year: 2021, entity: 'Archegos Capital', amount: '$10B', category: 'Hidden Exposure',
    country: 'United States', coords: [-74.006, 40.7128],
    summary: 'Family office used total return swaps to build hidden $20B+ exposure without disclosure.',
    broke: 'Swap structures hid position size from brokers. No aggregated view of cross-broker exposure.',
    allowed: 'Total return swaps legally avoided position disclosure. No structural requirement for aggregate exposure visibility.',
    cost: '$10 billion in losses across 6 prime brokers. Credit Suisse lost $5.5B alone.',
    granullo: 'All positions are visible regardless of instrument structure. Cross-broker exposure is aggregated automatically. No derivative masks position size.',
    prevention: '$10B investment fund, disclosed positions only, after aggregate exposure verification and cross-broker reconciliation',
  },
  {
    year: 2021, entity: 'Pandora Papers', amount: '$11.3T', category: 'Hidden Exposure',
    country: 'United States', coords: [-77.0, 38.9],
    summary: 'Leak of 11.9M documents revealed offshore wealth hiding by world leaders and billionaires.',
    broke: 'Shell companies and trusts used to obscure beneficial ownership. No structural transparency requirement.',
    allowed: 'Legal structures permitted ownership concealment. No global standard for beneficial ownership disclosure.',
    cost: '$11.3 trillion held offshore. Tax revenue lost across every nation.',
    granullo: 'Every financial entity has declared beneficial ownership visible to relevant authorities. Shell structures cannot obscure true ownership.',
    prevention: '$11.3T offshore fund, declared ownership only, after beneficial ownership verification and transparency compliance',
  },
  {
    year: 2022, entity: 'Celsius Network', amount: '$4.7B', category: 'Broken Segregation',
    country: 'United States', coords: [-74.2591, 40.6976],
    summary: 'Crypto lending platform used customer deposits for risky DeFi strategies without disclosure.',
    broke: 'Customer deposits treated as company assets. No structural segregation between customer and firm capital.',
    allowed: 'Terms of service permitted commingling. No structural barrier existed between customer and company funds.',
    cost: '$4.7 billion in customer funds locked. 1.7 million users affected.',
    granullo: 'Customer funds are automatically kept separate. No terms of service override structural protection. Real-time confirmation of segregation.',
    prevention: '$4.7B customer fund, customer-approved deployments only, after segregation verification and real-time balance confirmation',
  },
  {
    year: 2022, entity: 'Terra/Luna', amount: '$40B', category: 'Unverified Leverage',
    country: 'South Korea', coords: [126.978, 37.5665],
    summary: 'Algorithmic stablecoin collapsed when the peg broke, wiping out entire ecosystem in 48 hours.',
    broke: 'Stability mechanism relied on circular logic. No real reserves backing the peg.',
    allowed: 'Self-referential collateral accepted as stable. No structural requirement for independent reserve confirmation.',
    cost: '$40 billion in market value destroyed. Multiple investor suicides. Triggered industry-wide contagion.',
    granullo: 'Stability claims require independently verified reserves. No circular collateral accepted. Structural proof of backing before any stability claim.',
    prevention: '$40B stablecoin fund, verified reserves only, after independent reserve confirmation and stress testing',
  },
  {
    year: 2023, entity: 'Signature Bank', amount: '$110B', category: 'Hidden Exposure',
    country: 'United States', coords: [-73.97, 40.75],
    summary: 'Heavy crypto exposure and concentrated deposits led to third-largest bank failure in US history.',
    broke: 'Deposit concentration in volatile sector unmonitored. Liquidity risk materialized faster than response capability.',
    allowed: 'No structural limit on sector concentration of deposits. Risk was acknowledged but not enforced.',
    cost: '$110 billion in assets. Closed by regulators. Uninsured depositors initially at risk.',
    granullo: 'Deposit concentration has structural limits. Sector exposure triggers automatic diversification requirements. Risk is enforced, not just reported.',
    prevention: '$110B banking fund, diversified deposits only, after concentration verification and liquidity stress testing',
  },
  {
    year: 2023, entity: 'Adani Group', amount: '$150B', category: 'Hidden Exposure',
    country: 'India', coords: [72.8777, 19.076],
    summary: 'Short-seller report alleged stock manipulation and undisclosed related-party transactions across conglomerate.',
    broke: 'Complex corporate structure obscured related-party flows. Offshore entities used to inflate stock holdings.',
    allowed: 'Multi-layered corporate structures legally permitted opacity. No unified view of cross-entity obligations.',
    cost: '$150 billion in market value lost in weeks. Bond ratings downgraded.',
    granullo: 'All related-party transactions are automatically declared and visible. Corporate structure complexity cannot obscure financial flows between entities.',
    prevention: '$150B corporate fund, disclosed entities only, after related-party verification and structural transparency checks',
  },
  // ═══ Additional historical events to create 100-year systemic pattern ═══
  { year: 1910, entity: 'Rubber Boom Crash', amount: '$100M', category: 'Unverified Leverage', country: 'United Kingdom', coords: [-0.12, 51.51], summary: 'Speculative rubber plantation investments collapsed when synthetic rubber emerged.', broke: 'No verification of actual plantation productivity or market demand.', allowed: 'Investment sold on projected yields without evidence.', cost: '$100M in losses. Multiple bank failures.', granullo: 'Investment returns require verified productivity data.', prevention: '$100M fund, verified assets only, after productivity verification' },
  { year: 1921, entity: 'Post-WWI Bank Failures', amount: '$3.5B', category: 'Hidden Exposure', country: 'United States', coords: [-90.19, 38.63], summary: '505 banks failed as wartime agricultural loans defaulted en masse.', broke: 'Concentrated agricultural lending without diversification.', allowed: 'No structural limit on sector concentration.', cost: '$3.5B in deposits lost. 505 banks closed.', granullo: 'Sector concentration has structural limits.', prevention: '$3.5B fund, diversified lending only, after concentration checks' },
  { year: 1925, entity: 'Florida Land Bubble', amount: '$700M', category: 'Fabricated Records', country: 'United States', coords: [-80.19, 25.76], summary: 'Speculative land deals collapsed when hurricane revealed fictitious property values.', broke: 'Property valuations based on speculative projections not reality.', allowed: 'Self-assessed property values accepted as collateral.', cost: '$700M in speculative losses.', granullo: 'Property valuations require independent verification.', prevention: '$700M real estate fund, verified valuations only, after independent appraisal' },
  { year: 1933, entity: 'Ivar Kreuger', amount: '$400M', category: 'Fabricated Records', country: 'Sweden', coords: [18.07, 59.33], summary: 'Match King built global monopoly on fabricated bonds and forged government securities.', broke: 'Securities created without backing. Auditors compromised.', allowed: 'Self-issued instruments accepted without independent verification.', cost: '$400M in fraudulent securities.', granullo: 'All securities require independently verified backing.', prevention: '$400M securities fund, verified instruments only, after independent audit' },
  { year: 1962, entity: 'Billie Sol Estes', amount: '$24M', category: 'Fabricated Records', country: 'United States', coords: [-101.85, 33.58], summary: 'Texas businessman obtained loans using mortgages on non-existent fertilizer tanks.', broke: 'Collateral never physically verified.', allowed: 'Paper documentation accepted without physical inspection.', cost: '$24M in fraudulent loans.', granullo: 'Physical collateral requires verified inspection.', prevention: '$24M loan fund, verified collateral only, after physical inspection' },
  { year: 1973, entity: 'Equity Funding', amount: '$2B', category: 'Fabricated Records', country: 'United States', coords: [-118.24, 34.05], summary: 'Insurance company created 64,000 fake policies to inflate revenue over 9 years.', broke: 'Policy counts self-reported. No independent verification of policyholder existence.', allowed: 'Computer-generated fake policies accepted by reinsurers.', cost: '$2B in fictitious insurance assets.', granullo: 'Policy creation requires verified policyholder identity.', prevention: '$2B insurance fund, verified policies only, after identity verification' },
  { year: 1975, entity: 'NYC Fiscal Crisis', amount: '$12B', category: 'Hidden Exposure', country: 'United States', coords: [-74.006, 40.71], summary: 'New York City nearly went bankrupt from accumulated hidden debt and budget manipulation.', broke: 'Budget deficits hidden through accounting manipulation and delayed reporting.', allowed: 'No real-time visibility into municipal financial position.', cost: '$12B in accumulated hidden debt.', granullo: 'Municipal budgets require real-time structural transparency.', prevention: '$12B municipal fund, transparent budgets only, after real-time reporting' },
  { year: 1985, entity: 'S&L Crisis (Phase 1)', amount: '$160B', category: 'Broken Segregation', country: 'United States', coords: [-96.80, 32.78], summary: 'Savings & Loan institutions gambled with depositor money after deregulation.', broke: 'Deposit insurance removed market discipline. Risky investments made with insured deposits.', allowed: 'Deregulation allowed S&Ls to invest deposits in speculative assets.', cost: '$160B taxpayer bailout. 1,043 institutions failed.', granullo: 'Deposit-taking institutions face structural investment limits.', prevention: '$160B deposit fund, approved investments only, after risk verification' },
  { year: 1987, entity: 'Black Monday', amount: '$500B', category: 'Systemic Collapse', country: 'United States', coords: [-74.01, 40.71], summary: 'Dow Jones fell 22.6% in a single day. Portfolio insurance amplified the crash.', broke: 'Automated selling triggered cascade. No circuit breakers existed.', allowed: 'Portfolio insurance strategies could execute unlimited sell orders simultaneously.', cost: '$500B in market value destroyed in one day.', granullo: 'Automated strategies have structural execution limits. Circuit breakers enforced.', prevention: '$500B market fund, limited automation only, after execution limit verification' },
  { year: 1989, entity: 'Lincoln Savings', amount: '$3.4B', category: 'Misused Funds', country: 'United States', coords: [-112.07, 33.45], summary: 'Charles Keating redirected S&L deposits into risky personal ventures.', broke: 'Depositor funds invested in undisclosed real estate. Regulators pressured by senators.', allowed: 'No structural barrier between deposits and company investments.', cost: '$3.4B. 23,000 bondholders lost savings.', granullo: 'Depositor funds automatically kept separate from company investments.', prevention: '$3.4B deposit fund, kept separate use only, after barrier verification' },
  { year: 1993, entity: 'Metallgesellschaft', amount: '$1.3B', category: 'Hidden Exposure', country: 'Germany', coords: [8.68, 50.11], summary: 'German conglomerate lost $1.3B on oil futures hedging strategy gone wrong.', broke: 'Long-term hedging positions required massive margin calls when oil prices dropped.', allowed: 'No structural limit on hedge exposure relative to firm capital.', cost: '$1.3B in hedging losses. Near-bankruptcy.', granullo: 'Hedging positions have structural limits relative to firm capital.', prevention: '$1.3B hedging fund, capital-limited positions only, after margin verification' },
  { year: 1996, entity: 'Sumitomo Copper', amount: '$2.6B', category: 'Hidden Exposure', country: 'Japan', coords: [135.50, 34.69], summary: 'Single trader manipulated global copper market for a decade.', broke: 'One individual controlled both trading and record-keeping. No separation of duties.', allowed: 'Trading authority and position reporting combined in single person.', cost: '$2.6B in unauthorized trading losses.', granullo: 'Trading and reporting are automatically separated. No single actor controls both.', prevention: '$2.6B trading fund, separated duties only, after role segregation verification' },
  { year: 1999, entity: 'Daewoo Group', amount: '$80B', category: 'Hidden Exposure', country: 'South Korea', coords: [126.98, 37.57], summary: 'Second-largest conglomerate collapsed under hidden debt of $80B.', broke: 'Cross-subsidization between subsidiaries obscured true financial position.', allowed: 'Conglomerate structure permitted opaque inter-company flows.', cost: '$80B in hidden debt. Largest corporate failure in history at time.', granullo: 'Inter-company flows require structural transparency and arm-length verification.', prevention: '$80B conglomerate fund, transparent flows only, after cross-entity verification' },
  { year: 2003, entity: 'Ahold', amount: '$1B', category: 'Fabricated Records', country: 'Netherlands', coords: [4.90, 52.37], summary: 'Dutch supermarket giant inflated earnings through fictitious vendor rebates.', broke: 'Vendor rebates recognized without verification of actual agreements.', allowed: 'Revenue recognized from unverified promotional allowances.', cost: '$1B in overstated earnings. Stock dropped 63%.', granullo: 'Revenue recognition requires verified counterparty confirmation.', prevention: '$1B retail fund, verified rebates only, after counterparty confirmation' },
  { year: 2004, entity: 'Oil-for-Food Scandal', amount: '$1.8B', category: 'Misused Funds', country: 'Iraq', coords: [44.37, 33.31], summary: 'UN humanitarian program manipulated with $1.8B in illicit surcharges and kickbacks.', broke: 'No structural verification that humanitarian funds reached intended recipients.', allowed: 'Program allowed intermediaries to extract fees without transparency.', cost: '$1.8B diverted from humanitarian aid.', granullo: 'Humanitarian funds have structural end-recipient verification.', prevention: '$1.8B aid fund, verified recipients only, after delivery confirmation' },
  { year: 2010, entity: 'Olympus Corporation', amount: '$1.7B', category: 'Fabricated Records', country: 'Japan', coords: [139.69, 35.69], summary: 'Japanese camera maker hid $1.7B in losses through fraudulent acquisitions for two decades.', broke: 'Acquisition payments routed to shell companies to hide investment losses.', allowed: 'Tobashi scheme used acquisition fees to absorb prior trading losses.', cost: '$1.7B hidden over 20 years. CEO fired for exposing it.', granullo: 'Acquisition payments require verified business purpose. No shell company routing.', prevention: '$1.7B corporate fund, verified acquisitions only, after business purpose verification' },
  { year: 2011, entity: 'Sino-Forest', amount: '$3B', category: 'Fabricated Records', country: 'China', coords: [116.40, 39.90], summary: 'Canadian-listed company claimed to own forests in China that did not exist.', broke: 'Asset claims never physically verified. Auditors relied on company documentation.', allowed: 'Self-reported forest ownership accepted without satellite or physical verification.', cost: '$3B in fictitious assets.', granullo: 'Physical asset claims require independent verification.', prevention: '$3B forestry fund, verified assets only, after physical inspection' },
  { year: 2014, entity: 'Petrobras', amount: '$2.1B', category: 'Misused Funds', country: 'Brazil', coords: [-43.17, -22.91], summary: 'Brazil state oil company at center of $2.1B bribery scheme involving politicians and executives.', broke: 'Procurement contracts inflated. Kickbacks embedded in project costs.', allowed: 'No structural verification that contract prices reflected market rates.', cost: '$2.1B in bribes. Dozens of politicians and executives jailed.', granullo: 'Procurement requires structural price verification against market benchmarks.', prevention: '$2.1B procurement fund, market-rate contracts only, after price verification' },
  { year: 2014, entity: 'BES (Banco Espirito Santo)', amount: '$5.7B', category: 'Hidden Exposure', country: 'Portugal', coords: [-9.14, 38.74], summary: 'Oldest private bank in Portugal collapsed under hidden losses from family holding company.', broke: 'Losses in family entities hidden from bank balance sheet.', allowed: 'Related party exposure obscured through complex corporate layers.', cost: '$5.7B in losses. Required emergency bailout.', granullo: 'Related party exposure requires structural transparency across all entities.', prevention: '$5.7B banking fund, transparent related parties only, after cross-entity verification' },
  { year: 2018, entity: 'CumEx Tax Fraud', amount: '$60B', category: 'Fabricated Records', country: 'Germany', coords: [9.99, 53.55], summary: 'Banks exploited dividend tax loophole to claim refunds on taxes never paid.', broke: 'Tax system could not track share ownership around dividend dates.', allowed: 'Multiple parties claimed tax refund on same dividend payment.', cost: '$60B in tax revenue stolen across European governments.', granullo: 'Tax claims require verified ownership at record date. No duplicate claims accepted.', prevention: '$60B tax fund, verified ownership only, after record date verification' },
  { year: 2019, entity: 'Mozambique Hidden Debt', amount: '$2B', category: 'Hidden Exposure', country: 'Mozambique', coords: [35.53, -15.40], summary: 'Government secretly guaranteed $2B in loans that were hidden from IMF and parliament.', broke: 'Sovereign debt issued without parliamentary approval or international disclosure.', allowed: 'No structural requirement for sovereign debt transparency.', cost: '$2B in hidden sovereign debt. IMF suspended support.', granullo: 'Sovereign debt requires structural disclosure to all stakeholders before issuance.', prevention: '$2B sovereign fund, disclosed debt only, after parliamentary and disclosure verification' },
  { year: 2020, entity: 'Luckin Coffee (II)', amount: '$150M', category: 'Fabricated Records', country: 'China', coords: [118.09, 24.48], summary: 'Internal investigation revealed additional $150M in fabricated expenses beyond initial disclosure.', broke: 'Expense approvals lacked verification. Internal audit compromised.', allowed: 'Self-reported expenses accepted without receipt verification.', cost: '$150M additional fabrication discovered.', granullo: 'Expenses require verified receipts and independent approval.', prevention: '$150M expense fund, verified receipts only, after independent approval' },
  { year: 2020, entity: 'Greensill Capital', amount: '$10B', category: 'Fabricated Records', country: 'United Kingdom', coords: [-0.08, 51.51], summary: 'Supply chain finance firm collapsed after insurance coverage withdrawn on $10B in receivables.', broke: 'Receivables financing based on prospective invoices that did not yet exist.', allowed: 'Future receivables accepted as current collateral without verification.', cost: '$10B in exposure. Credit Suisse froze $10B in funds.', granullo: 'Receivables financing requires verified existing invoices. No prospective collateral.', prevention: '$10B finance fund, verified receivables only, after invoice existence verification' },
  { year: 2022, entity: 'Three Arrows Capital', amount: '$3.5B', category: 'Unverified Leverage', country: 'Singapore', coords: [103.85, 1.29], summary: 'Crypto hedge fund defaulted on $3.5B in loans after leveraged bets on Terra/Luna collapsed.', broke: 'Borrowed from multiple lenders without any seeing total exposure.', allowed: 'No structural mechanism for lenders to verify total borrower exposure.', cost: '$3.5B in lender losses. Triggered cascading crypto contagion.', granullo: 'Borrower exposure is automatically aggregated. Lenders verify total obligations before extending credit.', prevention: '$3.5B lending fund, verified total exposure only, after aggregate obligation checks' },
  { year: 2023, entity: 'SVB', amount: '$209B', category: 'Hidden Exposure', country: 'United States', coords: [-121.89, 37.34], summary: 'Silicon Valley Bank collapsed after unrealized bond losses triggered deposit flight.', broke: 'Interest rate risk unhedged. Unrealized losses hidden in held-to-maturity portfolio.', allowed: 'Accounting rules permitted hiding unrealized losses if securities held to maturity.', cost: '$209B in assets. Second-largest US bank failure.', granullo: 'Interest rate exposure is automatically managed. No accounting classification hides real economic risk.', prevention: '$209B banking fund, hedged portfolios only, after rate risk verification' },
  { year: 2024, entity: 'Synapse Financial', amount: '$85M', category: 'Broken Segregation', country: 'United States', coords: [-122.09, 37.39], summary: 'Banking-as-a-service middleware failed leaving $85M in customer funds unaccounted for.', broke: 'Middleware layer obscured which bank held which customer funds.', allowed: 'No end-to-end fund tracking through middleware. Reconciliation was manual.', cost: '$85M in missing funds. 100,000+ users affected.', granullo: 'Fund flows have structural end-to-end visibility. No middleware layer can obscure location of customer money.', prevention: '$85M fintech fund, tracked funds only, after real-time reconciliation verification' },
  { year: 2019, entity: 'WeWork', amount: '$47B', category: 'Misused Funds', country: 'United States', coords: [-74.00, 40.74], summary: 'Valued at $47B before IPO collapse revealed unsustainable spending and governance failure.', broke: 'CEO had unchecked spending authority. No structural limits on capital deployment relative to revenue.', allowed: 'Venture capital accepted growth narratives without verified unit economics. Board oversight was absent.', cost: '$47 billion in peak valuation destroyed. Thousands laid off.', granullo: 'Capital deployment requires verified milestones. Spending authority has structural limits. Governance is enforced, not optional.', prevention: '$47B venture fund, milestone-verified spending only, after governance and unit economics verification' },
  { year: 2008, entity: '2008 Financial Crisis', amount: '$10T+', category: 'Systemic Collapse', country: 'United States', coords: [-74.01, 40.71], summary: 'Global financial system collapsed from unverified mortgage-backed securities and cascading leverage.', broke: 'Mortgages bundled and sold without verifying borrower ability to repay. Rating agencies compromised. Leverage unchecked.', allowed: 'No structural verification of underlying asset quality. Risk was distributed but never reduced.', cost: '$10 trillion+ in global wealth destroyed. 8.7 million jobs lost in the US alone.', granullo: 'Every financial instrument requires verified underlying asset quality. Leverage has structural limits. Risk cannot be hidden through repackaging.', prevention: '$10T+ financial system, verified assets only, after quality verification and structural leverage limits' },
];

/* ═══════════════════════ CATEGORY SYSTEM ═══════════════════════ */
const CATEGORY_REMAP = {
  'Fabricated Records': 'Fraud',
  'Misused Funds': 'Misuse',
  'Premature Release': 'Misuse',
  'Unverified Leverage': 'Leverage',
  'Systemic Collapse': 'Leverage',
  'Hidden Exposure': 'Governance',
  'Broken Segregation': 'Governance',
  'Broken Custody': 'Custody',
};

const EVENTS = RAW_EVENTS.map(e => ({ ...e, category: CATEGORY_REMAP[e.category] || e.category }));
const CATEGORIES = ['Fraud', 'Governance', 'Leverage', 'Misuse', 'Custody'];
/* ═══ SEMANTIC SEVERITY COLOR HIERARCHY ═══
 * Scarlet = Fraud (fabricated records, identity theft)
 * Blue    = Governance (compliance, hidden exposure, broken segregation)
 * Violet  = Leverage (systemic risk, unverified leverage)
 * Amber   = Misuse (fund diversion, drift)
 * Graphite = Custody (legacy custody failures)
 *
 * Dark mode uses desaturated jewel tones so the map reads as calm,
 * not as a grid of bright LEDs on pure black.
 */
const CATEGORY_COLORS_LIGHT = {
  Fraud: '#CD2200',
  Governance: '#2563EB',
  Leverage: '#7C3AED',
  Misuse: '#D97706',
  Custody: '#6B7280',
};
const CATEGORY_COLORS_DARK = {
  Fraud: '#B04A3A',       // muted crimson
  Governance: '#5775B8',  // dusty steel blue
  Leverage: '#8571B8',    // muted amethyst
  Misuse: '#B8864A',      // bronzed amber
  Custody: '#6B7280',     // graphite (unchanged — already neutral)
};
// Default (light) — kept as the module-level alias so any historical reference still works.
const CATEGORY_COLORS = CATEGORY_COLORS_LIGHT;

/** Theme-aware category palette. Each rendering component shadows the
 *  module const by calling this hook at its top. */
function useCategoryColors() {
  const { resolvedTheme } = useTheme();
  return resolvedTheme === 'dark' ? CATEGORY_COLORS_DARK : CATEGORY_COLORS_LIGHT;
}

/* Node size scaling by financial impact */
function nodeRadius(amount) {
  const v = parseLoss(amount);
  if (Number.isNaN(v)) return 4.5;
  if (v >= 100) return 8.5;
  if (v >= 10) return 7;
  if (v >= 1) return 5.5;
  return 4.5;
}

const YEAR_RANGE = [1910, 2024];

/* ═══════════════════════ CURATED FLASH SEQUENCE ═══════════════════════ */
const FLASH_SEQUENCE_DATA = [
  { entity: 'FTX', amount: '$8B', line: 'Customer funds used without consent.' },
  { entity: 'Wirecard', amount: '€1.9B', line: 'Revenue existed only on paper.' },
  { entity: 'Theranos', amount: '$10B', line: 'Technology never worked as claimed.' },
  { entity: 'WeWork', amount: '$47B', line: 'Capital spent without discipline or accountability.' },
  { entity: 'Lehman Brothers', amount: '$639B', line: 'Risk taken without understanding exposure.' },
  { entity: 'Enron', amount: '$74B', line: 'Losses hidden until collapse became inevitable.' },
  { entity: '2008 Financial Crisis', amount: '$10T+', line: 'System built on unchecked leverage.' },
  { entity: '1MDB', amount: '$4.5B', line: 'Public funds diverted for private use.' },
  { entity: 'Pandora Papers', amount: '$11.3T', line: 'Wealth hidden beyond oversight and accountability.' },
];
const FLASH_FINAL_LINE = 'Different names. Same failure.';
const FLASH_HOLD_MS = 2500;
const FLASH_CINEMATIC_HOLD_MS = 2000;
const FLASH_FINAL_HOLD_MS = 4000;
const FLASH_TRANSITION_MS = 400;

/* ═══════════════════════ LIVE PROOF ═══════════════════════ */
const LIVE_PROOF = [
  { outcome: 'Execution blocked — similar condition failure', amount: '$4.2M', ago: '3 min ago', allowed: false, ref: 'PRG-7A91D2' },
  { outcome: 'Funds released — all conditions satisfied', amount: '$12,400', ago: 'Just now', allowed: true, ref: 'PRG-4A29C1' },
  { outcome: 'Execution blocked — beneficiary unverified', amount: '\u20B92.4Cr', ago: '7 min ago', allowed: false, ref: 'PRG-7E3B12' },
  { outcome: 'Subsidy disbursed — conditions met', amount: '\u20B915,000', ago: '12 min ago', allowed: true, ref: 'PRG-8B12D7' },
  { outcome: 'Execution blocked — delivery not confirmed', amount: '$28,500', ago: '1 min ago', allowed: false, ref: 'PRG-AF1C44' },
  { outcome: 'Incentive authorized — all checks passed', amount: '$1,200', ago: '18 min ago', allowed: true, ref: 'PRG-5D92B1' },
  { outcome: 'Execution blocked — leverage limit exceeded', amount: '$890K', ago: '4 min ago', allowed: false, ref: 'PRG-D41F09' },
];

/* ═══════════════════════ UTILITIES ═══════════════════════ */
function parseLoss(amount) {
  const m = amount.match(/[\d.]+/);
  if (!m) return 0;
  const v = parseFloat(m[0]);
  if (amount.includes('T')) return v * 1000;
  if (amount.includes('B')) return v;
  if (amount.includes('M')) return v / 1000;
  return 0;
}

/* Density buckets for scrubber visualization */
const DENSITY = (() => {
  const buckets = [];
  for (let y = YEAR_RANGE[0]; y <= YEAR_RANGE[1]; y += 2) {
    const evts = EVENTS.filter(e => e.year >= y && e.year < y + 2);
    buckets.push({ year: y, count: evts.length, loss: evts.reduce((s, e) => s + parseLoss(e.amount), 0), events: evts });
  }
  return buckets;
})();
const MAX_DENSITY = Math.max(...DENSITY.map(b => b.count), 1);

/* Find nearest event year for gravity snapping */
function findNearestEventYear(year, events) {
  if (events.length === 0) return year;
  let closest = events[0];
  let minDist = Math.abs(events[0].year - year);
  for (let i = 1; i < events.length; i++) {
    const d = Math.abs(events[i].year - year);
    if (d < minDist) { minDist = d; closest = events[i]; }
  }
  return minDist <= 3 ? closest.year : year;
}

/* ═══════════════════════ REALIZATION COLLAPSE ═══════════════════════ */
function RealizationCollapse() {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    const timers = LIVE_PROOF.map((_, i) =>
      setTimeout(() => setVisible(i + 1), 800 + i * 500)
    );
    return () => timers.forEach(clearTimeout);
  }, []);
  if (visible === 0) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="mt-4 pt-3 space-y-1.5"
      style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}
      data-testid="realization-collapse"
    >
      {LIVE_PROOF.map((entry, i) => (
        i < visible && (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35 }}
            className="flex items-center justify-between py-1.5 px-2 rounded-md"
            style={{ background: 'rgba(var(--tl-text-rgb),0.015)' }}
            data-testid={`realization-entry-${i}`}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {entry.allowed
                ? <Check size={9} style={{ color: SYS_COLORS.allowed }} />
                : <X size={9} style={{ color: SYS_COLORS.blocked }} />
              }
              <span className="text-[10px] font-mono truncate" style={{ color: entry.allowed ? 'rgba(var(--tl-text-rgb),0.5)' : `${SYS_COLORS.blocked}99` }}>
                {entry.outcome}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <span className="text-[10px] font-mono" style={{ color: entry.allowed ? `${SYS_COLORS.allowed}70` : `${SYS_COLORS.blocked}70` }}>
                {entry.amount}
              </span>
              <span className="text-[8px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.15)' }}>
                {entry.ago}
              </span>
            </div>
          </motion.div>
        )
      ))}
    </motion.div>
  );
}

/* ═══════════════════════ TEMPORAL SCRUBBER ═══════════════════════ */

/** Smoothly tweens a displayed number toward `value`. Formatter decides how it renders. */
function TweenedNumber({ value, duration = 0.6, format = (v) => Math.round(v).toString(), className, style }) {
  const mv = useMotionValue(value);
  const [display, setDisplay] = useState(format(value));

  useEffect(() => {
    const controls = animateMotion(mv, value, {
      duration,
      ease: [0.22, 0.8, 0.24, 1],
      onUpdate: (v) => setDisplay(format(v)),
    });
    return controls.stop;
  }, [value, duration, format, mv]);

  return <span className={className} style={style}>{display}</span>;
}

function TemporalScrubber({ cursor, onScrub, onScrubEnd, playing, onTogglePlay, hoverYear, onHoverYear }) {
  const CATEGORY_COLORS = useCategoryColors();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const pct = (y) => ((y - YEAR_RANGE[0]) / (YEAR_RANGE[1] - YEAR_RANGE[0])) * 100;

  const yearFromX = useCallback((clientX) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r) return cursor;
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return YEAR_RANGE[0] + frac * (YEAR_RANGE[1] - YEAR_RANGE[0]);
  }, [cursor]);

  const onDown = useCallback((e) => {
    dragging.current = true;
    onScrub(yearFromX(e.clientX));
    trackRef.current?.setPointerCapture(e.pointerId);
  }, [onScrub, yearFromX]);

  const onMove = useCallback((e) => {
    if (dragging.current) {
      onScrub(yearFromX(e.clientX));
    } else {
      const r = trackRef.current?.getBoundingClientRect();
      if (r) {
        const frac = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
        const yr = Math.round(YEAR_RANGE[0] + frac * (YEAR_RANGE[1] - YEAR_RANGE[0]));
        onHoverYear(yr);
      }
    }
  }, [onScrub, yearFromX, onHoverYear]);

  const onUp = useCallback(() => {
    dragging.current = false;
    if (onScrubEnd) onScrubEnd();
  }, [onScrubEnd]);

  const onLeave = useCallback(() => { onHoverYear(null); }, [onHoverYear]);

  /* Hover info for the hovered year */
  const hoverBucket = hoverYear != null ? DENSITY.find(b => Math.abs(b.year - hoverYear) < 2) : null;

  /* Cumulative tally — failures + total loss accumulated through the cursor year */
  const cumulative = useMemo(() => {
    let events = 0;
    let loss = 0;
    for (const b of DENSITY) {
      if (b.year <= cursor) {
        events += b.count || 0;
        loss += b.loss || 0;
      }
    }
    return { events, loss };
  }, [cursor]);

  const formatLoss = (b) => {
    if (b >= 1000) return `$${(b / 1000).toFixed(1)}T`;
    if (b >= 1) return `$${b.toFixed(0)}B`;
    return `$${(b * 1000).toFixed(0)}M`;
  };

  /* Era chapters — subtle dividers to give the timeline weight */
  const ERAS = [
    { start: 1910, end: 1945, label: 'Industrial Era' },
    { start: 1945, end: 1980, label: 'Post-War' },
    { start: 1980, end: 2008, label: 'Globalization' },
    { start: 2008, end: 2024, label: 'Digital Era' },
  ];

  const DECADES = [1920, 1940, 1960, 1980, 2000, 2020];

  return (
    <div className="mt-8" data-testid="temporal-scrubber">
      {/* ═══ Top Row: Play CTA + Live Counter + Cinematic Year ═══ */}
      <div className="flex items-end justify-between mb-5 px-1 gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {/* Premium play button — pulsing ring while paused to invite the click */}
          <div className="relative">
            {!playing && (
              <motion.span
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{ border: `1px solid ${CATEGORY_COLORS.Fraud}` }}
                animate={{ scale: [1, 1.45, 1.45], opacity: [0.55, 0, 0] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
              />
            )}
            <button
              onClick={onTogglePlay}
              className="relative flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full text-[11px] font-mono font-medium uppercase tracking-[0.14em] transition-all duration-200"
              style={{
                background: playing
                  ? 'rgba(96,165,250,0.10)'
                  : isDark ? 'rgba(176,74,58,0.10)' : 'rgba(205,34,0,0.06)',
                border: `1px solid ${playing
                  ? 'rgba(96,165,250,0.35)'
                  : isDark ? 'rgba(176,74,58,0.55)' : 'rgba(205,34,0,0.35)'}`,
                color: playing
                  ? '#60a5fa'
                  : isDark ? '#C96A58' : '#CD2200',
                boxShadow: playing
                  ? '0 0 22px rgba(96,165,250,0.18), inset 0 0 0 1px rgba(96,165,250,0.05)'
                  : `0 0 22px ${isDark ? 'rgba(176,74,58,0.22)' : 'rgba(205,34,0,0.14)'}`,
              }}
              data-testid="play-history-btn"
            >
              <span className="flex items-center justify-center w-5 h-5 rounded-full" style={{
                background: playing ? 'rgba(96,165,250,0.18)' : isDark ? 'rgba(176,74,58,0.20)' : 'rgba(205,34,0,0.12)',
              }}>
                {playing ? <Pause size={10} /> : <Play size={10} style={{ marginLeft: 1 }} />}
              </span>
              {playing ? 'Pause' : 'Play the century'}
            </button>
          </div>

          {/* Live accumulator — feels like a ticker */}
          <div className="hidden sm:flex flex-col gap-0.5 leading-none">
            <span className="text-[9px] font-mono uppercase tracking-[0.2em]" style={{ color: 'rgba(var(--tl-text-rgb),0.32)' }}>
              Through {Math.round(cursor)} · accumulated
            </span>
            <div className="flex items-baseline gap-2" data-testid="scrubber-cumulative">
              <TweenedNumber
                value={cumulative.events}
                duration={playing ? 0.35 : 0.6}
                format={(v) => String(Math.round(v))}
                className="text-[15px] font-mono font-semibold tabular-nums"
                style={{ color: 'rgba(var(--tl-text-rgb),0.75)' }}
              />
              <span className="text-[10px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.35)' }}>failures</span>
              <span className="text-[10px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.18)' }}>·</span>
              <TweenedNumber
                value={cumulative.loss}
                duration={playing ? 0.35 : 0.6}
                format={formatLoss}
                className="text-[15px] font-mono font-semibold tabular-nums"
                style={{ color: CATEGORY_COLORS.Fraud }}
              />
              <span className="text-[10px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.35)' }}>lost</span>
            </div>
          </div>
        </div>

        {/* Giant cinematic year display */}
        <div className="flex flex-col items-end gap-0.5 leading-none">
          <span className="text-[9px] font-mono uppercase tracking-[0.28em]" style={{ color: 'rgba(var(--tl-text-rgb),0.3)' }}>
            Timeline year
          </span>
          <AnimatePresence>
            {hoverBucket && hoverBucket.count > 0 && !dragging.current && (
              <motion.span
                initial={{ opacity: 0, y: -2 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-[10px] font-mono"
                style={{ color: 'rgba(var(--tl-text-rgb),0.38)' }}
                data-testid="scrubber-hover-info"
              >
                {hoverYear} — {hoverBucket.count} failure{hoverBucket.count > 1 ? 's' : ''}
                {hoverBucket.loss > 0 ? ` — ${formatLoss(hoverBucket.loss)}` : ''}
              </motion.span>
            )}
          </AnimatePresence>
          <motion.span
            key={Math.round(cursor)}
            initial={{ opacity: 0.5, y: 1 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.12 }}
            className="text-[44px] sm:text-[52px] font-mono font-semibold tabular-nums tracking-tight"
            style={{
              color: 'rgba(var(--tl-text-rgb),0.88)',
              lineHeight: 1,
              textShadow: isDark
                ? '0 0 28px rgba(226,232,240,0.08)'
                : '0 1px 0 rgba(0,0,0,0.03)',
            }}
            data-testid="cursor-year"
          >
            {Math.round(cursor)}
          </motion.span>
        </div>
      </div>

      {/* ═══ Scrubber Track ═══ */}
      <div
        ref={trackRef}
        className="relative cursor-pointer select-none"
        style={{ height: 96, touchAction: 'none' }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onLeave}
        data-testid="scrubber-track"
      >
        {/* Era chapter dividers + labels (behind bars) */}
        {ERAS.map((era, i) => (
          <React.Fragment key={era.label}>
            {i > 0 && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: `${pct(era.start)}%`,
                  bottom: 22,
                  height: 52,
                  width: 1,
                  background: `linear-gradient(to bottom, transparent, rgba(var(--tl-text-rgb),${isDark ? '0.08' : '0.06'}), transparent)`,
                }}
              />
            )}
            <span
              className="absolute text-[8px] font-mono uppercase pointer-events-none"
              style={{
                left: `${pct((era.start + era.end) / 2)}%`,
                top: 0,
                transform: 'translateX(-50%)',
                color: 'rgba(var(--tl-text-rgb),0.22)',
                letterSpacing: '0.22em',
              }}
            >
              {era.label}
            </span>
          </React.Fragment>
        ))}

        {/* Density bars — failure landscape, taller and more dramatic */}
        <div className="absolute bottom-8 left-0 right-0 flex items-end gap-[1px]" style={{ height: 58 }}>
          {DENSITY.map((b, i) => {
            const h = b.count > 0 ? Math.max(4, (b.count / MAX_DENSITY) * 58) : 0;
            const active = b.year <= cursor;
            const near = Math.abs(b.year - cursor) < 4;
            const isHovered = hoverYear != null && Math.abs(b.year - hoverYear) < 2;
            const topCat = b.events.length > 0
              ? Object.entries(b.events.reduce((a, e) => { a[e.category] = (a[e.category] || 0) + 1; return a; }, {})).sort((a, b) => b[1] - a[1])[0]?.[0]
              : null;
            const col = topCat ? (CATEGORY_COLORS[topCat] || '#B8D7F0') : '#B8D7F0';
            // Gradient bar: intense top, fade to translucent at baseline
            const activeAlphaTop = near ? 'E0' : isHovered ? 'B0' : '70';
            const activeAlphaBot = near ? '20' : isHovered ? '18' : '10';
            return (
              <div
                key={i}
                className="flex-1 rounded-t-[2px]"
                style={{
                  height: h,
                  background: active
                    ? `linear-gradient(to top, ${col}${activeAlphaBot}, ${col}${activeAlphaTop})`
                    : `linear-gradient(to top, rgba(var(--tl-text-rgb),0.01), rgba(var(--tl-text-rgb),${isHovered ? '0.08' : '0.035'}))`,
                  transition: 'background 180ms ease, box-shadow 180ms ease',
                  boxShadow: near && active && h > 10
                    ? `0 -3px 14px ${col}30, inset 0 1px 0 ${col}50`
                    : 'none',
                }}
              />
            );
          })}
        </div>

        {/* Decade tick marks */}
        {DECADES.map(y => (
          <div
            key={`tick-${y}`}
            className="absolute pointer-events-none"
            style={{
              left: `${pct(y)}%`,
              bottom: 20,
              width: 1,
              height: 4,
              background: y <= cursor ? 'rgba(var(--tl-text-rgb),0.28)' : 'rgba(var(--tl-text-rgb),0.12)',
            }}
          />
        ))}

        {/* Baseline rule */}
        <div className="absolute bottom-5 left-0 right-0" style={{
          height: 1,
          background: `linear-gradient(to right,
            rgba(var(--tl-text-rgb),0.02),
            rgba(var(--tl-text-rgb),${isDark ? '0.18' : '0.14'}) 50%,
            rgba(var(--tl-text-rgb),0.02))`,
        }} />

        {/* Hover ghost-needle */}
        {hoverYear != null && (
          <div
            className="absolute bottom-5 w-px pointer-events-none"
            style={{
              left: `${pct(hoverYear)}%`,
              transform: 'translateX(-50%)',
              height: 62,
              background: `linear-gradient(to top, rgba(var(--tl-text-rgb),0.28), rgba(var(--tl-text-rgb),0.02))`,
            }}
          />
        )}

        {/* Playhead — cinematic needle */}
        <div
          className="absolute bottom-5 z-10"
          style={{
            left: `${pct(cursor)}%`,
            transform: 'translateX(-50%)',
            transition: dragging.current ? 'none' : 'left 100ms ease-out',
          }}
        >
          <div
            className="mx-auto"
            style={{
              width: 1,
              height: 70,
              background: `linear-gradient(to top, ${CATEGORY_COLORS.Fraud}, ${CATEGORY_COLORS.Fraud}00)`,
            }}
          />
          <div className="relative -mt-2 flex items-center justify-center">
            <motion.span
              className="absolute rounded-full pointer-events-none"
              style={{ width: 14, height: 14, border: `1px solid ${CATEGORY_COLORS.Fraud}` }}
              animate={{ scale: [1, 1.6, 1.6], opacity: [0.5, 0, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
            />
            <div
              className="rounded-full"
              style={{
                width: 10,
                height: 10,
                background: CATEGORY_COLORS.Fraud,
                boxShadow: `0 0 12px ${CATEGORY_COLORS.Fraud}90, 0 0 2px ${CATEGORY_COLORS.Fraud}`,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.7)'}`,
              }}
            />
          </div>
        </div>

        {/* Decade labels — legible, uppercase mono */}
        {DECADES.map(y => (
          <span
            key={y}
            className="absolute text-[10px] font-mono font-medium tabular-nums"
            style={{
              left: `${pct(y)}%`,
              bottom: -4,
              transform: 'translateX(-50%)',
              letterSpacing: '0.08em',
              color: y <= cursor
                ? 'rgba(var(--tl-text-rgb),0.5)'
                : 'rgba(var(--tl-text-rgb),0.22)',
              transition: 'color 220ms ease',
            }}
          >
            {y}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════ ROTATING SPOTLIGHT ═══════════════════════ */
function RotatingSpotlight({ events }) {
  const CATEGORY_COLORS = useCategoryColors();
  const [idx, setIdx] = useState(0);
  const notable = useMemo(
    () => events.filter(e => parseLoss(e.amount) >= 5).sort((a, b) => parseLoss(b.amount) - parseLoss(a.amount)).slice(0, 8),
    [events]
  );
  useEffect(() => {
    if (notable.length === 0) return;
    const id = setInterval(() => setIdx(i => (i + 1) % notable.length), 4000);
    return () => clearInterval(id);
  }, [notable.length]);
  if (notable.length === 0) return null;
  const event = notable[idx % notable.length];
  return (
    <div className="flex items-center gap-3 py-2.5 px-1 min-h-[36px]" data-testid="rotating-spotlight">
      <AnimatePresence mode="wait">
        <motion.div
          key={event.entity}
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 4 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3"
        >
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[event.category] }} />
          <span className="text-[11px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.2)' }}>{event.year}</span>
          <span className="text-[12px]" style={{ color: 'rgba(var(--tl-text-rgb),0.35)' }}>{event.entity}</span>
          <span className="text-[11px] font-mono font-semibold" style={{ color: `${CATEGORY_COLORS[event.category]}80` }}>{event.amount}</span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════ IMPACT LABEL — floating name+amount near pin ═══════════════════════ */
function ImpactLabel({ event }) {
  const CATEGORY_COLORS = useCategoryColors();
  const col = CATEGORY_COLORS[event.category] || '#B8D7F0';
  return (
    <motion.g
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <text
        textAnchor="middle"
        y={-18}
        style={{ fontFamily: 'monospace', fontSize: '7px', fill: col, fontWeight: 600, pointerEvents: 'none' }}
      >
        {event.entity}
      </text>
      <text
        textAnchor="middle"
        y={-10}
        style={{ fontFamily: 'monospace', fontSize: '6px', fill: `${col}99`, fontWeight: 500, pointerEvents: 'none' }}
      >
        {event.amount}
      </text>
    </motion.g>
  );
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */
export default function HistoricalTimeline() {
  const { injectProgram, parsedProgram } = useGranulloIntelligence();
  const { resolvedTheme } = useTheme();
  const CATEGORY_COLORS = useCategoryColors();
  const isDark = resolvedTheme === 'dark';
  const mapRef = useRef(null);
  const hauntTimers = useRef([]);
  const autoPlayRef = useRef(null);

  /* Temporal state */
  const [cursorYear, setCursorYear] = useState(YEAR_RANGE[1]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrubberHoverYear, setScrubberHoverYear] = useState(null);

  /* Selection + haunting */
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeCategories, setActiveCategories] = useState(new Set(CATEGORIES));
  const [hoverInfo, setHoverInfo] = useState(null);
  const [hauntPhase, setHauntPhase] = useState(0);
  const [blockSimChecks, setBlockSimChecks] = useState([]);
  const [blockSimVisible, setBlockSimVisible] = useState(0);

  const prevCursorInt = useRef(YEAR_RANGE[1]);

  /* Pattern reinforcement overlay */
  const [showPatternMsg, setShowPatternMsg] = useState(false);
  const [patternLabel, setPatternLabel] = useState('');
  const revealedCount = useRef(0);
  const clickedCategories = useRef({});

  /* ═══ Flash Event Sequence State ═══ */
  const [flashIndex, setFlashIndex] = useState(0);       // 0..8 = events, 9 = final frame
  const [flashVisible, setFlashVisible] = useState(true);
  const [flashPaused, setFlashPaused] = useState(false);
  const [cinematicMode, setCinematicMode] = useState(false);
  const [showFinalCta, setShowFinalCta] = useState(false);
  const flashTimerRef = useRef(null);

  /* Resolve flash sequence from EVENTS */
  const flashSequence = useMemo(() =>
    FLASH_SEQUENCE_DATA.map(fd => {
      const ev = EVENTS.find(e => e.entity === fd.entity);
      return ev ? { ...ev, microCopy: fd.line } : null;
    }).filter(Boolean),
    []
  );
  const currentFlashEvent = flashIndex < flashSequence.length ? flashSequence[flashIndex] : null;
  const isFlashFinalFrame = flashIndex >= flashSequence.length;

  /* Derived */
  const cursorInt = Math.round(cursorYear);
  const visibleEvents = useMemo(
    () => EVENTS.filter(e => e.year <= cursorInt && activeCategories.has(e.category)),
    [cursorInt, activeCategories]
  );
  const totalLoss = useMemo(() => visibleEvents.reduce((s, e) => s + parseLoss(e.amount), 0), [visibleEvents]);

  /* Track newly revealed events for pattern reinforcement */
  useEffect(() => {
    const prevYear = prevCursorInt.current;
    if (cursorInt > prevYear) {
      const newEvents = EVENTS.filter(
        e => e.year > prevYear && e.year <= cursorInt && activeCategories.has(e.category)
      );
      if (newEvents.length > 0) {
        revealedCount.current += newEvents.length;
        /* Pattern reinforcement after 5-8 events during autoplay */
        if (isPlaying && revealedCount.current >= 6 && revealedCount.current <= 9) {
          setShowPatternMsg(true);
          setTimeout(() => setShowPatternMsg(false), 3000);
        }
      }
    }
    prevCursorInt.current = cursorInt;
  }, [cursorInt, activeCategories, isPlaying]);

  /* Auto-play with adaptive speed — faster in dense periods, slower in sparse */
  useEffect(() => {
    if (!isPlaying) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }
    const tick = () => {
      setCursorYear(prev => {
        if (prev >= YEAR_RANGE[1]) {
          setIsPlaying(false);
          return YEAR_RANGE[1];
        }
        /* Adaptive speed: check density around current year */
        const nearbyEvents = EVENTS.filter(e => Math.abs(e.year - prev) < 3).length;
        const speed = nearbyEvents > 3 ? 0.15 : nearbyEvents > 1 ? 0.25 : 0.4;
        return Math.min(prev + speed, YEAR_RANGE[1]);
      });
    };
    autoPlayRef.current = setInterval(tick, 80);
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [isPlaying]);

  /* ═══ Flash Event Sequence Timer ═══ */
  useEffect(() => {
    if (flashPaused || selectedEvent || showFinalCta) return;
    const holdMs = isFlashFinalFrame
      ? FLASH_FINAL_HOLD_MS
      : (cinematicMode ? FLASH_CINEMATIC_HOLD_MS : FLASH_HOLD_MS);
    flashTimerRef.current = setTimeout(() => {
      setFlashVisible(false);
      setTimeout(() => {
        setFlashIndex(prev => {
          const next = prev + 1;
          if (next > flashSequence.length) {
            /* Cycle complete */
            if (cinematicMode) {
              setCinematicMode(false);
              setShowFinalCta(true);
              return flashSequence.length; // stay on final frame
            }
            return 0; // loop in ambient mode
          }
          return next;
        });
        setFlashVisible(true);
      }, FLASH_TRANSITION_MS);
    }, holdMs);
    return () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); };
  }, [flashIndex, flashPaused, selectedEvent, isFlashFinalFrame, flashSequence.length, cinematicMode, showFinalCta]);

  /* Close detail if event scrolled past */
  useEffect(() => {
    if (selectedEvent && selectedEvent.year > cursorInt) {
      setSelectedEvent(null);
      setHauntPhase(0);
      hauntTimers.current.forEach(clearTimeout);
    }
  }, [cursorInt, selectedEvent]);

  /* ─── Handlers ─── */
  const handleSelect = useCallback((event) => {
    setIsPlaying(false);
    setFlashPaused(true);
    hauntTimers.current.forEach(clearTimeout);
    /* Freeze + highlight moment (300ms) then open panel */
    setCursorYear(event.year);
    setHauntPhase(0);
    setBlockSimChecks([]);
    setBlockSimVisible(0);

    /* Pattern detection: track categories clicked */
    clickedCategories.current[event.category] = (clickedCategories.current[event.category] || 0) + 1;
    if (clickedCategories.current[event.category] >= 3) {
      const catName = CATEGORIES.find(c => c === event.category) || event.category;
      setPatternLabel(`You've seen this pattern before.`);
      setShowPatternMsg(true);
      setTimeout(() => setShowPatternMsg(false), 6000);
    }

    setTimeout(() => {
      setSelectedEvent(event);
      const t = [];
      t.push(setTimeout(() => setHauntPhase(1), 200));
      t.push(setTimeout(() => setHauntPhase(2), 1200));
      t.push(setTimeout(() => setHauntPhase(3), 2400));
      t.push(setTimeout(() => {
        setHauntPhase(4);
        if (event.prevention) {
          const parsed = parseProgram(event.prevention);
          if (parsed) {
            const bad = { name: 'Unauthorized transfer', amount: 50000, allowed: false };
            const checks = generateChecks(parsed, bad);
            setBlockSimChecks(checks);
            checks.forEach((_, i) => t.push(setTimeout(() => setBlockSimVisible(i + 1), 3400 + i * 350)));
          }
        }
      }, 3000));
      hauntTimers.current = t;
    }, 300);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedEvent(null);
    setHauntPhase(0);
    setFlashPaused(false);
    hauntTimers.current.forEach(clearTimeout);
  }, []);

  const navigateEvent = useCallback((dir) => {
    if (!selectedEvent) return;
    const idx = visibleEvents.findIndex(e => e.entity === selectedEvent.entity && e.year === selectedEvent.year);
    const next = dir === 'next' ? (idx + 1) % visibleEvents.length : (idx - 1 + visibleEvents.length) % visibleEvents.length;
    handleSelect(visibleEvents[next]);
  }, [selectedEvent, visibleEvents, handleSelect]);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => {
      if (!prev) {
        revealedCount.current = 0;
        if (cursorYear >= YEAR_RANGE[1] - 1) setCursorYear(YEAR_RANGE[0]);
        setSelectedEvent(null);
        setHauntPhase(0);
      }
      return !prev;
    });
  }, [cursorYear]);

  const toggleCategory = useCallback((cat) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) { if (next.size > 1) next.delete(cat); } else next.add(cat);
      return next;
    });
  }, []);

  /* ═══ Cinematic Mode Trigger ═══ */
  const startCinematic = useCallback(() => {
    setCinematicMode(true);
    setShowFinalCta(false);
    setFlashIndex(0);
    setFlashVisible(true);
    setFlashPaused(false);
    setSelectedEvent(null);
    setHauntPhase(0);
  }, []);

  const handleScrub = useCallback((year) => {
    setCursorYear(year);
    setIsPlaying(false);
  }, []);

  /* Gravity snap — when scrub ends, gently attract to nearest event */
  const handleScrubEnd = useCallback(() => {
    setCursorYear(prev => {
      const snapped = findNearestEventYear(Math.round(prev), EVENTS);
      return snapped;
    });
  }, []);

  const preventThis = useCallback((event) => {
    if (!event?.prevention) return;
    injectProgram(event.prevention, 'timeline');
    const el = document.getElementById('try-granullo');
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 72;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [injectProgram]);

  return (
    <section className="g-timeline relative py-14 sm:py-20 px-6" style={{ background: 'var(--tl-bg, #F5F5F5)', minHeight: '900px' }} data-testid="historical-timeline"
      {...lensAnnotation({
        id: 'historical-timeline',
        type: LENS_TYPES.TIMELINE_EVENT,
        title: '400 Years of Payment Failure Atlas',
        meaning: 'Maps 400 years of real payment misuse events across the globe. Each incident demonstrates what happens when money moves without structural verification.',
        actions: ['Explore timeline events', 'View failure patterns', 'Run Granullo simulation on any event'],
        relations: ['consequence-memory', 'structural-fix'],
        state: 'evidence-layer',
        priority: 'high',
      })}
    >
      <div className="max-w-[1200px] mx-auto">

        {/* ═══ Header ═══ */}
        <div className="mb-6">
          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.5 }}>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2" style={{ color: 'var(--tl-heading, #e2e8f0)' }} data-testid="timeline-title">
              Historical Timeline of Financial Failures
            </h2>
            <p className="text-[15px] max-w-[560px] mb-2" style={{ color: 'rgba(var(--tl-text-rgb),0.45)' }}>
              Different companies. <RebelRedWordOverlay rotate={-1.5} offsetY={-2}>Same mistakes.</RebelRedWordOverlay> Same missing rules.
            </p>
            <p className="text-[12px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.25)' }}>
              {EVENTS.length} real failures. 1910–2024. ${Math.round(totalLoss)}B+ lost.
            </p>
          </motion.div>
        </div>

        {/* ═══ Category Filters ═══ */}
        <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="timeline-filters">
          {CATEGORIES.map(cat => {
            const active = activeCategories.has(cat);
            const col = CATEGORY_COLORS[cat];
            const count = visibleEvents.filter(e => e.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150"
                style={{
                  background: active ? `${col}08` : 'transparent',
                  border: `1px solid ${active ? `${col}25` : 'rgba(var(--tl-text-rgb),0.04)'}`,
                  color: active ? `${col}cc` : 'rgba(var(--tl-text-rgb),0.2)',
                  opacity: active ? 1 : 0.6,
                }}
                data-testid={`filter-${cat.toLowerCase()}`}
              >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: active ? col : 'rgba(var(--tl-text-rgb),0.15)' }} />
                {cat}
                <span className="text-[9px] font-mono ml-1" style={{ color: active ? `${col}80` : 'rgba(var(--tl-text-rgb),0.1)' }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* ═══ "See how this keeps happening" CTA ═══ */}
        <div className="flex items-center justify-between mb-3">
          <div />
          <button
            onClick={startCinematic}
            className="flex items-center gap-1.5 text-[11px] font-mono font-medium transition-all duration-200 px-3 py-1.5 rounded cursor-pointer"
            style={{
              color: (cinematicMode || showFinalCta) ? 'rgba(239,68,68,0.6)' : 'rgba(var(--tl-text-rgb),0.3)',
              background: (cinematicMode || showFinalCta) ? 'rgba(239,68,68,0.06)' : 'transparent',
              border: `1px solid ${(cinematicMode || showFinalCta) ? 'rgba(239,68,68,0.12)' : 'rgba(var(--tl-text-rgb),0.04)'}`,
            }}
            onMouseEnter={e => { if (!cinematicMode && !showFinalCta) { e.currentTarget.style.color = 'rgba(var(--tl-text-rgb),0.5)'; e.currentTarget.style.borderColor = 'rgba(var(--tl-text-rgb),0.1)'; }}}
            onMouseLeave={e => { if (!cinematicMode && !showFinalCta) { e.currentTarget.style.color = 'rgba(var(--tl-text-rgb),0.3)'; e.currentTarget.style.borderColor = 'rgba(var(--tl-text-rgb),0.04)'; }}}
            data-testid="cinematic-cta"
          >
            {cinematicMode
              ? (isFlashFinalFrame ? 'Complete' : `${flashIndex + 1} of ${flashSequence.length}`)
              : showFinalCta ? 'Complete' : 'See how this keeps happening'} <ArrowUpRight size={11} />
          </button>
        </div>

        {/* ═══ MAP — Primary Visual ═══ */}
        <div
          ref={mapRef}
          className="relative rounded-lg overflow-hidden"
          style={{ background: 'var(--tl-surface, #FFFFFF)', border: '1px solid rgba(var(--tl-text-rgb),0.03)' }}
          data-testid="world-map"
        >
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 145, center: [10, 22] }}
            width={960}
            height={500}
            style={{ width: '100%', height: 'auto' }}
          >
            <ZoomableGroup>
              <Geographies geography={GEO_URL}>
                {({ geographies }) => geographies.map(geo => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="var(--tl-geo-fill)"
                    stroke="var(--tl-geo-stroke)"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { fill: 'var(--tl-geo-hover)', outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))}
              </Geographies>

              {/* ═══ Event dots — alive, inviting, unmistakably clickable ═══ */}
              {visibleEvents.map((ev, idx) => {
                const col = CATEGORY_COLORS[ev.category] || '#B8D7F0';
                const isSel = selectedEvent?.entity === ev.entity && selectedEvent?.year === ev.year;
                const isFlash = currentFlashEvent?.entity === ev.entity && currentFlashEvent?.year === ev.year;
                const isHov = hoverInfo?.data?.entity === ev.entity && hoverInfo?.data?.year === ev.year;
                const baseR = nodeRadius(ev.amount);
                // Stagger pulse phase so neighbors don't tick in unison (more organic)
                const pulseDelay = `-${(idx * 0.37) % 3.2}s`;
                return (
                  <Marker
                    key={`d-${ev.entity}-${ev.year}`}
                    coordinates={ev.coords}
                    onMouseEnter={(e) => {
                      setFlashPaused(true);
                      const r = mapRef.current?.getBoundingClientRect();
                      if (r) setHoverInfo({ type: 'event', data: ev, x: e.clientX - r.left, y: e.clientY - r.top });
                    }}
                    onMouseLeave={() => { if (!selectedEvent) setFlashPaused(false); setHoverInfo(null); }}
                    onClick={() => handleSelect(ev)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Expanding echo — always on, staggered per-event */}
                    {!isSel && !cinematicMode && (
                      <circle r={baseR} fill="transparent" stroke={col} strokeWidth={0.7} opacity={0}>
                        <animate attributeName="r" values={`${baseR};${baseR + 8};${baseR + 8}`} dur="2.8s" begin={pulseDelay} repeatCount="indefinite" />
                        <animate attributeName="opacity" values={isDark ? "0.32;0;0" : "0.55;0;0"} dur="2.8s" begin={pulseDelay} repeatCount="indefinite" />
                      </circle>
                    )}

                    {/* Selected state — crisp orbiting ring */}
                    {isSel && (
                      <>
                        <circle r={14} fill="transparent" stroke={col} strokeWidth={0.8} opacity={0.22}>
                          <animate attributeName="r" values="14;18;14" dur="2s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.22;0.05;0.22" dur="2s" repeatCount="indefinite" />
                        </circle>
                        <circle r={9} fill="transparent" stroke={col} strokeWidth={1} opacity={0.4} />
                      </>
                    )}

                    {/* Outer soft glow — breathing halo */}
                    <circle
                      r={(isSel ? 9 : baseR + 1.5) + (isHov ? 2 : 0)}
                      fill={col}
                      opacity={isSel ? (isDark ? 0.14 : 0.18) : isHov ? (isDark ? 0.22 : 0.3) : cinematicMode ? 0.08 : (isDark ? 0.12 : 0.18)}
                      style={{
                        filter: `blur(${isSel || isHov ? 2.5 : 1.5}px)`,
                        transition: 'all 280ms cubic-bezier(0.2,0.8,0.2,1)',
                      }}
                    />

                    {/* Main dot — larger, solid, with glossy center */}
                    <circle
                      r={(isSel ? 6 : baseR) + (isHov ? 1.5 : 0)}
                      fill={col}
                      opacity={isSel ? 1 : cinematicMode ? 0.35 : (isDark ? 0.78 : 0.92)}
                      style={{
                        transition: 'all 260ms cubic-bezier(0.2,0.8,0.2,1)',
                        filter: isSel || isHov ? `drop-shadow(0 0 ${isSel ? 10 : 6}px ${col}${isDark ? '70' : 'A0'})` : `drop-shadow(0 0 3px ${col}${isDark ? '35' : '50'})`,
                      }}
                    />

                    {/* Inner bright highlight — gives the "gem" feel */}
                    <circle
                      r={Math.max(1, (isSel ? 2.8 : baseR * 0.45) + (isHov ? 0.6 : 0))}
                      cx={-baseR * 0.25}
                      cy={-baseR * 0.25}
                      fill="#FFFFFF"
                      opacity={isDark ? (isSel || isHov ? 0.28 : 0.14) : (isSel || isHov ? 0.55 : 0.35)}
                      style={{ transition: 'all 260ms ease' }}
                    />

                    {isSel && <ImpactLabel event={ev} />}
                  </Marker>
                );
              })}

              {/* ═══ Flash Event Highlight (foreground layer) ═══ */}
              {currentFlashEvent && flashVisible && !selectedEvent && (
                <Marker coordinates={currentFlashEvent.coords}>
                  <circle r={6} fill="transparent" stroke={CATEGORY_COLORS[currentFlashEvent.category] || '#B8D7F0'} strokeWidth={1.2} opacity={0.35}>
                    <animate attributeName="r" values="6;24;24" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.35;0;0" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle r={7} fill={CATEGORY_COLORS[currentFlashEvent.category] || '#B8D7F0'} opacity={0.9}
                    style={{ filter: `drop-shadow(0 0 12px ${CATEGORY_COLORS[currentFlashEvent.category] || '#B8D7F0'}80)` }}
                  />
                </Marker>
              )}
            </ZoomableGroup>
          </ComposableMap>

          {/* Hover tooltip */}
          <AnimatePresence>
            {hoverInfo && !selectedEvent && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
                className="absolute pointer-events-none z-10 rounded-md px-3 py-2"
                style={{
                  left: Math.min(hoverInfo.x + 12, (mapRef.current?.clientWidth || 800) - 240),
                  top: Math.max(8, hoverInfo.y - 8),
                  background: 'var(--tl-surface, #FFFFFF)',
                  border: '1px solid rgba(var(--tl-text-rgb),0.08)',
                }}
                data-testid="map-tooltip"
              >
                {hoverInfo.type === 'event' && (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: CATEGORY_COLORS[hoverInfo.data.category] }} />
                      <span className="text-[12px] font-semibold" style={{ color: '#e2e8f0' }}>{hoverInfo.data.entity}</span>
                      <span className="text-[10px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.3)' }}>{hoverInfo.data.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-mono font-bold" style={{ color: CATEGORY_COLORS[hoverInfo.data.category] }}>{hoverInfo.data.amount}</span>
                      <span className="text-[10px]" style={{ color: 'rgba(var(--tl-text-rgb),0.3)' }}>{hoverInfo.data.category}</span>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ═══ Flash Event Label with Micro-Copy + Progress ═══ */}
          <AnimatePresence>
            {currentFlashEvent && flashVisible && !selectedEvent && !hoverInfo && (
              <motion.div
                key={`flash-${flashIndex}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="absolute left-5 bottom-12 pointer-events-none z-10"
                data-testid="flash-event-label"
              >
                {cinematicMode && (
                  <span className="text-[10px] font-mono block mb-2" style={{ color: 'rgba(var(--tl-text-rgb),0.25)' }} data-testid="flash-progress">
                    {flashIndex + 1} of {flashSequence.length}
                  </span>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: CATEGORY_COLORS[currentFlashEvent.category] }} />
                  <span className="text-[13px] font-semibold" style={{ color: '#e2e8f0' }}>{currentFlashEvent.entity}</span>
                  <span className="text-[11px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.3)' }}>{currentFlashEvent.year}</span>
                </div>
                <span className="text-[22px] font-mono font-bold tracking-tight block" style={{ color: CATEGORY_COLORS[currentFlashEvent.category], textShadow: `0 0 20px ${CATEGORY_COLORS[currentFlashEvent.category]}40` }}>
                  {currentFlashEvent.amount}
                </span>
                {currentFlashEvent.microCopy && (
                  <span className="text-[12px] block mt-1.5 max-w-[280px] leading-snug" style={{ color: 'rgba(var(--tl-text-rgb),0.4)' }}>
                    {currentFlashEvent.microCopy}
                  </span>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ═══ Final Frame Overlay ═══ */}
          <AnimatePresence>
            {isFlashFinalFrame && flashVisible && !selectedEvent && (
              <motion.div
                key="flash-final"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-10"
                style={{ background: 'rgba(6,9,16,0.65)' }}
                data-testid="flash-final-frame"
              >
                <motion.span
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="text-[28px] sm:text-[36px] font-mono font-bold tracking-tight"
                  style={{ color: '#EFD6B8', textShadow: '0 0 30px rgba(239,68,68,0.25)' }}
                >
                  $16T+ lost
                </motion.span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                  className="text-[13px] sm:text-[15px] font-medium mt-2 tracking-wide"
                  style={{ color: 'rgba(var(--tl-text-rgb),0.4)' }}
                >
                  {FLASH_FINAL_LINE}
                </motion.span>
                {showFinalCta && (
                  <motion.button
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    onClick={() => {
                      const ev = flashSequence[Math.floor(Math.random() * flashSequence.length)];
                      if (ev) {
                        setShowFinalCta(false);
                        setFlashIndex(0);
                        handleSelect(ev);
                      }
                    }}
                    className="mt-5 px-5 py-2 rounded text-[13px] font-medium cursor-pointer"
                    style={{ background: 'rgba(239,68,68,0.12)', color: '#EFD6B8', border: '1px solid rgba(239,68,68,0.2)' }}
                    data-testid="final-frame-cta"
                  >
                    Run one of these failures &rarr;
                  </motion.button>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pattern reinforcement overlay — Rebel V2 */}
          <AnimatePresence>
            {showPatternMsg && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none z-30"
                data-testid="pattern-reinforcement"
              >
                <span className="text-[14px] font-mono font-medium tracking-wide px-4 py-2 rounded-lg" style={{ color: 'rgba(var(--tl-text-rgb),0.4)', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
                  {patternLabel || "You've seen this pattern before."}
                </span>
                <span style={{ fontFamily: '"Kalam", cursive', fontSize: '13px', color: 'var(--rebel-red)', fontWeight: 400, opacity: 0.5 }}>
                  same pattern
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Map bottom stats */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
            <motion.span
              className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full font-mono font-semibold text-[11px] uppercase tracking-[0.16em]"
              style={{
                color: isDark ? '#E2A090' : '#CD2200',
                background: isDark ? 'rgba(176,74,58,0.14)' : 'rgba(205,34,0,0.06)',
                border: `1px solid ${isDark ? 'rgba(176,74,58,0.45)' : 'rgba(205,34,0,0.28)'}`,
                boxShadow: isDark
                  ? '0 0 28px rgba(176,74,58,0.28)'
                  : '0 0 22px rgba(205,34,0,0.10)',
                backdropFilter: 'blur(6px)',
              }}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              data-testid="map-instruction"
            >
              <motion.span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full"
                style={{
                  background: isDark ? 'rgba(176,74,58,0.35)' : 'rgba(205,34,0,0.18)',
                }}
                animate={{ scale: [1, 1.12, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <MousePointerClick size={11} strokeWidth={2.2} />
              </motion.span>
              <span>Tap any gem</span>
              <motion.span
                className="inline-block"
                animate={{ x: [0, 3, 0] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                →
              </motion.span>
              <span style={{ opacity: 0.85 }}>see what broke</span>
            </motion.span>
            <span className="text-[10px] font-mono tabular-nums" style={{ color: 'rgba(var(--tl-text-rgb),0.4)' }}>
              {visibleEvents.length} failures visible
            </span>
          </div>

          {/* ═══ DETAIL PANEL — overlays right side of map ═══ */}
          <AnimatePresence>
            {selectedEvent && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="absolute right-0 top-0 bottom-0 w-full sm:w-[400px] z-20 overflow-y-auto"
                style={{
                  background: 'rgba(7,11,20,0.96)',
                  backdropFilter: 'blur(16px)',
                  borderLeft: '1px solid rgba(var(--tl-text-rgb),0.04)',
                }}
                data-testid="event-detail"
              >
                {/* Subtle background processing glow — linked to event category color */}
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: `radial-gradient(ellipse at 30% 50%, ${CATEGORY_COLORS[selectedEvent.category] || SYS_COLORS.blocked}08, transparent 70%)` }}
                  animate={{ opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Header bar — accent linked to event category color */}
                <div className="relative px-5 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${CATEGORY_COLORS[selectedEvent.category] || 'rgba(var(--tl-text-rgb),0.03)'}15` }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[selectedEvent.category] }} />
                    <div className="min-w-0">
                      <h3 className="text-[15px] font-semibold truncate" style={{ color: '#e2e8f0' }} data-testid="event-entity">{selectedEvent.entity}</h3>
                      <span className="text-[11px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.25)' }}>
                        {selectedEvent.year} · {selectedEvent.country} · {selectedEvent.category}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="shrink-0 p-1.5 rounded-md transition-colors hover:bg-white/5"
                    style={{ color: 'rgba(var(--tl-text-rgb),0.3)' }}
                    data-testid="close-detail"
                  >
                    <X size={14} />
                  </button>
                </div>

                {/* ─── Failure Intelligence Panel ─── */}
                <div className="relative p-5 space-y-4">

                  {/* Section 1: Event Header — Loss + Summary */}
                  <AnimatePresence>
                    {hauntPhase >= 1 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                        <motion.span
                          initial={{ opacity: 0, scale: 1.3 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 180, damping: 14 }}
                          className="block text-3xl font-mono font-bold tracking-tight mb-1"
                          style={{ color: CATEGORY_COLORS[selectedEvent.category] }}
                          data-testid="event-amount"
                        >
                          {selectedEvent.amount}
                        </motion.span>
                        <p className="text-[10px] font-mono uppercase tracking-[0.12em] mb-3" style={{ color: 'rgba(var(--tl-text-rgb),0.2)' }}>
                          Financial impact
                        </p>
                        <motion.p
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.15, duration: 0.3 }}
                          className="text-[13px] leading-relaxed"
                          style={{ color: 'rgba(var(--tl-text-rgb),0.55)' }}
                        >
                          {selectedEvent.summary}
                        </motion.p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Section 2: What Actually Happened — step-by-step */}
                  <AnimatePresence>
                    {hauntPhase >= 2 && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="space-y-2.5 pt-3"
                        style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}
                      >
                        <span className="text-[9px] font-mono uppercase tracking-[0.12em]" style={{ color: '#EFD6B880' }}>What actually happened</span>
                        <div className="space-y-1.5">
                          {selectedEvent.broke.split('. ').filter(s => s.trim()).map((step, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -6 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.15, duration: 0.25 }}
                              className="flex items-start gap-2"
                            >
                              <span className="text-[10px] mt-0.5 shrink-0" style={{ color: '#EFD6B850' }}>&#x2022;</span>
                              <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(239,68,68,0.65)' }}>{step.trim().replace(/\.$/, '')}</p>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Section 3: What Was Missing — 2–3 clear items */}
                  <AnimatePresence>
                    {hauntPhase >= 2 && selectedEvent.allowed && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.35 }}
                        className="space-y-2.5 pt-3"
                        style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}
                      >
                        <span className="text-[9px] font-mono uppercase tracking-[0.12em]" style={{ color: `${SYS_COLORS.blocked}60` }}>What was missing</span>
                        <div className="space-y-1.5">
                          {selectedEvent.allowed.split('. ').filter(s => s.trim()).slice(0, 3).map((item, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -6 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.4 + i * 0.15, duration: 0.25 }}
                              className="flex items-start gap-2"
                            >
                              <X size={10} className="mt-0.5 shrink-0" style={{ color: `${SYS_COLORS.blocked}50` }} />
                              <p className="text-[12px] leading-relaxed" style={{ color: `${SYS_COLORS.blocked}80` }}>{item.trim().replace(/\.$/, '')}</p>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Section 3b: What this cost — financial + human impact */}
                  <AnimatePresence>
                    {hauntPhase >= 2 && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.35 }}
                        className="pt-3"
                        style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}
                      >
                        <span className="text-[9px] font-mono uppercase tracking-[0.12em]" style={{ color: `${SYS_COLORS.intelligence}50` }}>What this cost</span>
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.8, duration: 0.5 }}
                          className="text-[13px] leading-relaxed mt-2"
                          style={{ color: `${SYS_COLORS.intelligence}70` }}
                          data-testid="event-cost"
                        >
                          {selectedEvent.cost}
                        </motion.p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Section 4: What Granullo Would Have Done — BLOCKED */}
                  <AnimatePresence>
                    {hauntPhase >= 3 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                        className="pt-3"
                        style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Shield size={13} style={{ color: SYS_COLORS.allowed }} />
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                            className="text-[9px] font-mono uppercase tracking-[0.12em]"
                            style={{ color: `${SYS_COLORS.allowed}80` }}
                            data-testid="counterfactual-statement"
                          >
                            What Granullo would have done
                          </motion.span>
                        </div>
                        <motion.p
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5, duration: 0.5 }}
                          className="text-[15px] font-semibold leading-snug mb-3"
                          style={{ color: 'rgba(var(--tl-text-rgb),0.65)' }}
                          data-testid="signature-line"
                        >
                          {selectedEvent.entity}: structurally prevented before consequence.
                        </motion.p>
                        <p className="text-[13px] leading-relaxed mb-3" style={{ color: `${SYS_COLORS.allowed}90` }} data-testid="event-granullo">
                          {selectedEvent.granullo}
                        </p>
                        {/* Prevented consequence — event-specific */}
                        <motion.div
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.7, duration: 0.4 }}
                          className="mt-3 p-3 rounded-lg"
                          style={{ background: `${CATEGORY_COLORS[selectedEvent.category] || SYS_COLORS.allowed}06`, border: `1px solid ${CATEGORY_COLORS[selectedEvent.category] || SYS_COLORS.allowed}12` }}
                          data-testid="prevented-consequence"
                        >
                          <span className="text-[8px] font-mono uppercase tracking-[0.1em] block mb-1" style={{ color: `${CATEGORY_COLORS[selectedEvent.category] || SYS_COLORS.allowed}80` }}>Prevented consequence</span>
                          <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(var(--tl-text-rgb),0.55)' }}>
                            {selectedEvent.cost}
                          </p>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Section 5: Condition checks — animated evaluation */}
                  <AnimatePresence>
                    {hauntPhase >= 4 && blockSimChecks.length > 0 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} data-testid="timeline-sim">
                        <span className="text-[9px] font-mono uppercase tracking-[0.12em] block mb-3" style={{ color: `${SYS_COLORS.system}40` }}>
                          Checking rules
                        </span>
                        <div className="space-y-2 mb-3">
                          {blockSimChecks.map((check, i) => (
                            i < blockSimVisible && (
                              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}
                                className="flex items-center gap-2.5 py-0.5">
                                {check.pass ? <Check size={11} style={{ color: SYS_COLORS.allowed }} /> : <X size={11} style={{ color: SYS_COLORS.blocked }} />}
                                <span className="text-[12px] font-mono" style={{ color: check.pass ? `${SYS_COLORS.allowed}BB` : `${SYS_COLORS.blocked}BB` }}>{check.text}</span>
                              </motion.div>
                            )
                          ))}
                        </div>
                        {blockSimVisible >= blockSimChecks.length && (
                          <>
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                              className="py-3 px-4 rounded-lg"
                              style={{ background: `${SYS_COLORS.blocked}08`, border: `1px solid ${SYS_COLORS.blocked}18`, boxShadow: `0 0 20px ${SYS_COLORS.blocked}0A` }}
                              data-testid="timeline-blocked-verdict"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <X size={13} style={{ color: SYS_COLORS.blocked }} />
                                <span className="text-[12px] font-mono font-semibold" style={{ color: SYS_COLORS.blocked }}>BLOCKED</span>
                              </div>
                              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.4 }}
                                className="text-[11px] font-mono" style={{ color: `${SYS_COLORS.blocked}70` }}>
                                This would not have been allowed to proceed.
                              </motion.p>
                            </motion.div>
                            <RealizationCollapse />
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Primary CTA — Experience this collapse */}
                <div className="relative px-5 py-3" style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}>
                  {selectedEvent.prevention ? (
                    <button
                      onClick={() => {
                        /* Dispatch custom event for auto-trigger in Try Granullo */
                        window.dispatchEvent(new CustomEvent('granullo:experience-collapse', {
                          detail: { text: selectedEvent.prevention, entity: selectedEvent.entity, source: 'timeline' }
                        }));
                        injectProgram(selectedEvent.prevention, 'timeline');
                        const el = document.getElementById('try-granullo');
                        if (el) { const top = el.getBoundingClientRect().top + window.pageYOffset - 72; window.scrollTo({ top, behavior: 'smooth' }); }
                      }}
                      className="flex items-center gap-2 text-[12px] font-mono font-semibold transition-all duration-200 py-2 px-3 rounded-lg"
                      style={{
                        color: SYS_COLORS.blocked,
                        background: `${SYS_COLORS.blocked}08`,
                        border: `1px solid ${SYS_COLORS.blocked}15`,
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = `${SYS_COLORS.blocked}14`;
                        e.currentTarget.style.borderColor = `${SYS_COLORS.blocked}30`;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = `${SYS_COLORS.blocked}08`;
                        e.currentTarget.style.borderColor = `${SYS_COLORS.blocked}15`;
                      }}
                      data-testid="run-failure-cta"
                    >
                      Run this exact failure <ArrowUpRight size={13} />
                    </button>
                  ) : null}
                </div>

                {/* Navigation */}
                <div className="relative flex items-center justify-between px-5 py-3" style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}>
                  <button onClick={() => navigateEvent('prev')} className="flex items-center gap-1 text-[11px] font-mono transition-colors" style={{ color: 'rgba(var(--tl-text-rgb),0.25)' }} data-testid="event-prev">
                    <ChevronLeft size={12} /> Previous
                  </button>
                  <span className="text-[10px] font-mono" style={{ color: 'rgba(var(--tl-text-rgb),0.15)' }}>
                    {visibleEvents.findIndex(e => e.entity === selectedEvent.entity && e.year === selectedEvent.year) + 1} / {visibleEvents.length}
                  </span>
                  <button onClick={() => navigateEvent('next')} className="flex items-center gap-1 text-[11px] font-mono transition-colors" style={{ color: 'rgba(var(--tl-text-rgb),0.25)' }} data-testid="event-next">
                    Next <ChevronRight size={12} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ═══ Dark-mode reflective caption — only surfaces in dark theme ═══ */}
        {isDark && (
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="mt-5 text-center text-[12px] tracking-[0.14em] uppercase font-mono"
            style={{ color: 'rgba(var(--tl-text-rgb),0.34)', letterSpacing: '0.18em' }}
            data-testid="timeline-dark-caption"
          >
            A century of the same failure. Different names.
          </motion.p>
        )}

        {/* Rotating spotlight — alive state when nothing selected */}
        {!selectedEvent && <RotatingSpotlight events={visibleEvents} />}

        {/* ═══ Temporal Scrubber ═══ */}
        <TemporalScrubber
          cursor={cursorYear}
          onScrub={handleScrub}
          onScrubEnd={handleScrubEnd}
          playing={isPlaying}
          onTogglePlay={togglePlay}
          hoverYear={scrubberHoverYear}
          onHoverYear={setScrubberHoverYear}
        />

        {/* ═══ Narrative Anchor — "Different names. Same failure." ═══ */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 1.2 }}
          className="mt-10 pt-8 text-center"
          style={{ borderTop: '1px solid rgba(var(--tl-text-rgb),0.03)' }}
          data-testid="narrative-anchor"
        >
          {/* Headline */}
          <p className="text-[22px] sm:text-[28px] font-semibold tracking-tight" style={{ color: 'var(--tl-heading, rgba(var(--tl-text-rgb),0.6))' }}>
            Different names. <RebelRedWordOverlay rotate={-2} offsetY={-3}>Same failure.</RebelRedWordOverlay>
          </p>

          {/* Theorem line */}
          <p className="text-[13px] mt-2 max-w-[480px] mx-auto" style={{ color: 'rgba(var(--tl-text-rgb),0.45)', fontStyle: 'italic' }}>
            The instruments evolved. The failure logic didn't.
          </p>

          {/* Hand-drawn failure recurrence graph */}
          <div className="mt-8 mb-6">
            <FailureRecurrenceGraph />
          </div>

          {/* Scarlet inevitability statement */}
          <p className="text-[13px] font-mono max-w-[480px] mx-auto" style={{ color: 'rgba(239,68,68,0.55)' }}>
            Every cycle grew from the same missing proof layer.
          </p>

          {/* Root cause anchor */}
          <p className="mt-3" style={{ fontFamily: '"Kalam", cursive', fontSize: '13px', color: 'var(--rebel-red)', fontWeight: 400, opacity: 0.45 }}>
            every decade, same root cause
          </p>

          <button
            onClick={() => {
              const el = document.getElementById('try-granullo');
              if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 72, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-mono transition-colors"
            style={{ color: 'rgba(239,68,68,0.5)' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#EFD6B8'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(239,68,68,0.5)'; }}
            data-testid="try-granullo-footer-cta"
          >
            Try it now <ArrowUpRight size={11} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

